// Base libs
import {
    Dispatch,
    PayloadAction,
    createAsyncThunk,
    createSelector,
    createSlice,
    isAnyOf,
} from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import type { Locale } from "i18n";
import { range } from "lodash";
import { denormalize, normalize } from "normalizr";
import { QualityApi } from "@genesys/cloud-frontend/apis/QualityApi";
import {
    AiScoringSettings,
    AssessmentForm,
    AssessmentFormFromJSON,
    AssessmentFormQuestionGroup,
    AssistanceCondition,
    AssistanceConditionOperatorEnum,
    EvaluationForm,
    EvaluationFormEvaluationFormTypeEnum,
    EvaluationFormResponse,
    EvaluationFormResponseFromJSON,
    EvaluationFormResponseWeightModeEnum,
    EvaluationFormWeightModeEnum,
    EvaluationQuestion,
    EvaluationQuestionAutomatedScoringFocusEnum,
    EvaluationQuestionGroup,
    EvaluationQuestionTypeEnum,
    EvaluationSettings,
    EvaluationSettingsAssigneeTypeEnum,
    QuestionGroupSettings,
    QuestionSettings,
    SurveyForm,
    SurveyFormFromJSON,
    SurveyQuestionGroup,
    SurveyQuestionTypeEnum,
    VisibilityConditionCombiningOperationEnum,
} from "@genesys/cloud-frontend/models";

// Base Commons
import { RootState } from "app/store";
import { DefaultLimits } from "constants/DefaultLimits";
import AutomatedScoringType from "enums/AutomatedScoringType";
import FeatureToggle from "enums/FeatureToggle";
import FormType from "enums/FormType";
import ModuleStatus from "enums/ModuleStatus";
import QualityFormPermission from "enums/qualityFormPermission";
import { addAnswerInfoToVisibilityPredicates } from "react/utils/qualityFormNormalizationUtil";
import type { DistributedOmit } from "react/utils/tsUtil";
import apiCore from "services/apiCore";
import embeddingService from "services/embeddingService";
import toastService from "services/toastService";
import {
    createEmptyAgreementRateData,
    getFormAgreementRate,
    getQuestionAgreementRates,
    getLatestPublishedForm,
} from "util/agreementRateUtils";
import {
    calculateGroupWeight,
    calculateScalingFactorValue,
    isNonNegativeInteger,
    isNonNegativeNumber,
} from "util/NumberInputOperator";

// Feature slices
import {
    selectPermission,
    selectHasQualityEvaluationSearchAny,
    selectCanUserViewSTATopics,
} from "features/auth/currentUserSlice";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import {
    selectMCQQuestionScoreAnswer,
    selectNPSQuestionScoreAnswer,
    selectQuestionGroupMarkedNa,
    selectScorable,
} from "features/scorable/scorableSlice";
import { selectTemplate } from "features/templates/templateSlice";
import {
    selectUnpublishedTopics,
    topicSliceActions,
} from "features/topics/topicsSlice";

// Form Editor commons
import { BaseQuestionType } from "./common/BaseQuestionType";
import NPSScoreCategories from "./formEditor/common/NPSScoreCategories";
import {
    QuestionTypesMapSurveyIndex,
    getQuestionTypesMap,
} from "./formEditor/common/QuestionType";
import { selectIsInCopyingViewMode } from "./formEditor/copyFormSlice";
import { SearchResult } from "./formEditor/sidePanel/SearchDropdown";
import { normalizedQualityForm } from "./qualityFormNormalizationSchemas";
import {
    selectHasAddPermissions,
    selectHasAIScoringEditPermission,
} from "./qualityFormPermissionSelectors";
import { selectIsFormValid } from "./qualityFormValidations";

// States
import {
    aiScoringSettingState,
    evaluationSettingsState,
    QualityFormState,
} from "./qualityFormSlices/qualityFormStates";

// Agreement Rate Types
import { AgreementRateData } from "./qualityFormSlices/qualityFormStates";
import { QuestionAgreementRates } from "util/agreementRateUtils";

// Types
import {
    ExpandedVisibilityCondition,
    FormNames,
    FormQuestion,
    FormQuestionGroup,
    VisibilityConditionPredicate,
    TemplateQuestion,
} from "./qualityFormSlices/qualityFormTypes";

// Slice Commons
import {
    isUndefinedOrNull,
    isVisibilityConditionEligibleQuestionType,
    selectAnswerOption,
    selectAnswerOptionMutable,
    selectAnswerOptionsForQuestion,
    selectAssistanceConditions,
    selectCurrentAnswerIdsForMSQ,
    selectForm,
    selectFormType,
    selectIsAutomatedEvaluationForm,
    selectQuestion,
    selectScorableQuestion,
    selectSelectedOptionIdForMSQ,
    hasAnyCriticalQuestions,
} from "./qualityFormSlices/qualityFormCommons";
import { API_CONFIGURATION } from "util/apiUtils";

export type DependentIds = {
    questionGroupIds: Array<string>;
    questionIds: Array<string>;
};

export type VisibilityConflictsOnMove = {
    questionIds: Array<string>;
    conflictingQuestionsWithinQuestionGroup?: Record<string, Array<string>>;
};

export type TopicFilteringFactor = {
    topicIdIdxMap: { [key: string]: number };
    hasSameTopicInDifferentConditions: boolean;
};

// values of this enum are the keys within the string file
export enum EntityType {
    QuestionGroup = "questionGroup",
    Question = "question",
    Answer = "answer",
}

type formQuestionGroupsState = Record<
    string,
    DistributedOmit<FormQuestionGroup, "questions" | "visibilityCondition"> & {
        questions: Array<string>;
        visibilityCondition?: ExpandedVisibilityCondition;
        showEditMode?: boolean;
    }
>;

type formQuestionsState = Record<
    string,
    DistributedOmit<
        FormQuestion,
        | "answerOptions"
        | "multipleSelectOptionQuestions"
        | "visibilityCondition"
    > & {
        answerOptions: Array<string>;
        visibilityCondition?: ExpandedVisibilityCondition;
        showEditMode?: boolean;
        automatedScoringType?: AutomatedScoringType;
        automatedScoringFocus?: EvaluationQuestionAutomatedScoringFocusEnum;
    }
>;

type LoadFormArg<TFormType = FormType> = TFormType extends FormType.ASSESSMENT
    ? {
          formType: FormType.ASSESSMENT;
          formId: string;
          moduleStatus?: ModuleStatus;
          assessmentForm?: AssessmentForm;
          isArchived?: boolean;
      }
    : TFormType extends FormType.SURVEY
    ? {
          formType: FormType.SURVEY;
          formId: string;
          language?: string;
      }
    : {
          formType: FormType.EVALUATION;
          formId: string;
          evaluationFormType?: EvaluationFormEvaluationFormTypeEnum;
      };

export type EvaluationSettingsErrorPayload = {
    id: string;
    settingsError: boolean;
    evaluationSettings: evaluationSettingsState;
};

export type AiScoringSettingsErrorPayload = {
    aiScoringSettingsError: boolean;
    isAiScoringEditPermissionError: boolean;
    aiScoringSettings: AiScoringSettings;
    evaluationForm: EvaluationFormResponse;
};

function getQuestionGroupContextIdMappingReverse(
    questionGroups: formQuestionGroupsState | undefined
): { [key: string]: string } {
    const result = {};

    if (!questionGroups) {
        return result;
    }

    for (const [questionGroupId, questionGroup] of Object.entries(
        questionGroups
    ) ?? []) {
        const questionGroupContextId = questionGroup.contextId;

        if (questionGroupContextId) {
            result[questionGroupContextId] = questionGroupId;
        }
    }

    return result;
}

function getQuestionContextIdMappingReverse(
    questions: formQuestionsState | undefined
): {
    [key: string]: string;
} {
    const result = {};

    if (!questions) {
        return result;
    }

    for (const [questionId, question] of Object.entries(questions) ?? []) {
        const questionContextId = question.contextId;

        if (questionContextId) {
            result[questionContextId] = questionId;
        }
    }

    return result;
}

function getQuestionGroupIdIndexMapping(
    questionGroupIdList: string[] | undefined
): { [key: string]: number } {
    const result = {};
    for (const [index, questionGroupId] of questionGroupIdList?.entries() ??
        []) {
        result[questionGroupId] = index;
    }
    return result;
}

function getQuestionIdIndexMapping(questionIdList: string[] | undefined): {
    [key: string]: number;
} {
    const result = {};
    for (const [index, questionId] of questionIdList?.entries() ?? []) {
        result[questionId] = index;
    }
    return result;
}

function getQuestionGroupIndexMapping(
    questionGroups: EvaluationQuestionGroup[] | undefined
): { [key: number]: EvaluationQuestionGroup } {
    const result = {};

    if (!questionGroups) {
        return result;
    }

    for (const [index, questionGroup] of questionGroups.entries() ?? []) {
        result[index] = questionGroup;
    }

    return result;
}

function getQuestionIndexMapping(questions: EvaluationQuestion[] | undefined): {
    [key: number]: EvaluationQuestion;
} {
    const result = {};

    if (!questions) {
        return result;
    }

    for (const [index, question] of questions.entries()) {
        result[index] = question;
    }

    return result;
}

function toIndexAiScoringSettings(
    settings: aiScoringSettingState | undefined,
    questionGroupIdList: string[] | undefined,
    questionGroups: formQuestionGroupsState | undefined
): AiScoringSettings {
    const result: AiScoringSettings = {};
    const questionGroupSettings: QuestionGroupSettings[] = [];

    if (
        settings?.questionGroupSettings &&
        questionGroupIdList &&
        questionGroups
    ) {
        // question group mapping
        const questionGroupIndices =
            getQuestionGroupIdIndexMapping(questionGroupIdList);
        // question mapping
        let questionIndices: { [key: string]: number } = {};
        for (const questionGroup of Object.values(questionGroups)) {
            // concat result from getQuestionIndexMapping with questionIndices
            questionIndices = {
                ...questionIndices,
                ...getQuestionIdIndexMapping(questionGroup.questions),
            };
        }

        for (const questionGroupSetting of Object.entries(
            settings.questionGroupSettings
        )) {
            const [questionGroupId, questionSettings] = questionGroupSetting;

            const formAiQuestionSettings: QuestionSettings[] = [];
            for (const questionSetting of Object.entries(
                questionSettings.questionSettings
            )) {
                const [questionId, questionSettingValue] = questionSetting;
                const setting = {
                    settings: questionSettingValue,
                };
                setting["questionIndex"] = questionIndices[questionId];
                formAiQuestionSettings.push(setting);
            }
            if (formAiQuestionSettings.length > 0) {
                const groupSetting = {
                    questionSettings: formAiQuestionSettings,
                };
                groupSetting["questionGroupIndex"] =
                    questionGroupIndices[questionGroupId];
                questionGroupSettings.push(groupSetting);
            }
        }

        result.questionGroupSettings = questionGroupSettings;
    }

    return result;
}

function toContextIdAiScoringSettings(
    initialAiSettings: AiScoringSettings | undefined,
    questionGroups: EvaluationQuestionGroup[] | undefined
): AiScoringSettings {
    const result: AiScoringSettings = {};
    const questionGroupSettings: QuestionGroupSettings[] = [];

    if (initialAiSettings?.questionGroupSettings && questionGroups) {
        const questionGroupIndexMapping =
            getQuestionGroupIndexMapping(questionGroups);

        for (const questionGroupSetting of initialAiSettings.questionGroupSettings.values()) {
            const questionGroupIndex =
                questionGroupSetting["questionGroupIndex"];
            const questionSettings = questionGroupSetting.questionSettings;
            if (questionGroupIndex !== undefined && questionSettings) {
                const questionGroup =
                    questionGroupIndexMapping[questionGroupIndex];

                if (questionGroup) {
                    const questionGroupContextId = questionGroup.contextId;
                    const questionIndexMapping = getQuestionIndexMapping(
                        questionGroup.questions
                    );

                    const formAiQuestionSettings: QuestionSettings[] = [];
                    for (const questionSetting of questionSettings.values()) {
                        const questionIndex = questionSetting["questionIndex"];

                        if (questionIndex !== undefined) {
                            const question =
                                questionIndexMapping[questionIndex];

                            if (question && question.contextId) {
                                formAiQuestionSettings.push({
                                    questionContextId: question.contextId,
                                    settings: questionSetting.settings,
                                });
                            }
                        }
                    }

                    if (formAiQuestionSettings.length > 0) {
                        questionGroupSettings.push({
                            questionGroupContextId: questionGroupContextId,
                            questionSettings: formAiQuestionSettings,
                        });
                    }
                }
            }
        }
        result.questionGroupSettings = questionGroupSettings;
    }

    return result;
}

function toFormAiScoringSettings(
    settings: aiScoringSettingState | undefined,
    questionGroups: formQuestionGroupsState | undefined,
    questions: formQuestionsState | undefined
): AiScoringSettings {
    const result = {};
    const questionGroupSettings: QuestionGroupSettings[] = [];

    if (settings?.questionGroupSettings && questionGroups && questions) {
        // iterate through settings.questionGroupSettings
        for (const questionGroupSetting of Object.entries(
            settings.questionGroupSettings
        )) {
            const [questionGroupId, questionSettings] = questionGroupSetting;
            const questionGroupContextId =
                questionGroups[questionGroupId].contextId;
            if (questionGroupContextId !== undefined) {
                const formAiQuestionSettings: QuestionSettings[] = [];
                for (const questionSetting of Object.entries(
                    questionSettings.questionSettings
                )) {
                    const [questionId, questionSettingValue] = questionSetting;
                    const questionContextId = questions[questionId].contextId;
                    if (questionContextId !== undefined) {
                        formAiQuestionSettings.push({
                            questionContextId: questionContextId,
                            settings: questionSettingValue,
                        });
                    }
                }
                if (formAiQuestionSettings.length > 0) {
                    questionGroupSettings.push({
                        questionGroupContextId: questionGroupContextId,
                        questionSettings: formAiQuestionSettings,
                    });
                }
            }
        }

        result["questionGroupSettings"] = questionGroupSettings;
    }

    return result;
}

function fromFormAiScoringSettings(
    settings: AiScoringSettings | undefined,
    questionGroups: formQuestionGroupsState | undefined,
    questions: formQuestionsState | undefined
): aiScoringSettingState {
    const result = { questionGroupSettings: {} };
    if (settings && questionGroups && questions) {
        const questionGroupContextIds =
            getQuestionGroupContextIdMappingReverse(questionGroups);
        const questionContextIds =
            getQuestionContextIdMappingReverse(questions);

        // iterate through settings.questionGroupSettings
        for (const questionGroupSetting of settings.questionGroupSettings ??
            []) {
            const questionGroupId =
                questionGroupSetting.questionGroupContextId !== undefined
                    ? questionGroupContextIds[
                          questionGroupSetting.questionGroupContextId
                      ]
                    : undefined;
            if (questionGroupId) {
                const newFormattedQuestionSettings = {};
                for (const questionSetting of questionGroupSetting.questionSettings ??
                    []) {
                    const questionId =
                        questionSetting.questionContextId !== undefined
                            ? questionContextIds[
                                  questionSetting.questionContextId
                              ]
                            : undefined;
                    if (questionId) {
                        newFormattedQuestionSettings[questionId] =
                            questionSetting.settings;
                    }
                }
                if (newFormattedQuestionSettings) {
                    result.questionGroupSettings[questionGroupId] = {
                        questionSettings: newFormattedQuestionSettings,
                    };
                }
            }
        }
    }

    return result;
}

// Helper function to ensure defaultAnswersTo is initialized
function getInitializedDefaultAnswersTo(questionGroup: any) {
    if (!questionGroup.defaultAnswersTo) {
        questionGroup.defaultAnswersTo = {
            highestScore: questionGroup.defaultAnswersToHighest || false,
            lowestScore: false,
            notApplicable: questionGroup.defaultAnswersToNA || false,
            userDefined: false,
        };
    }
    return questionGroup.defaultAnswersTo;
}

// Helper functions
function disableNAForQuestions(state: any, questionIds: string[] | undefined) {
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm({
        qualityForm: state,
    } as RootState);

    if (!isAutomatedEvaluationForm) return;
    questionIds?.forEach((id) => {
        const question = state.data.questions?.[id];
        if (question) {
            question.naEnabled = false;
        }
    });
}

export const loadForm = createAsyncThunk(
    "qualityForm/loadForm",
    async ({ formType, formId, ...rest }: LoadFormArg, { rejectWithValue }) => {
        if (formType === FormType.EVALUATION) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            if (formId === "new") {
                return {
                    id: "new",
                    contextId: uuidv4(),
                    name: "",
                    weightMode: EvaluationFormResponseWeightModeEnum.SCALED,
                    questionGroups: [
                        {
                            id: uuidv4(),
                            name: "",
                            type: "questionGroup",
                            questions: [],
                            weight: 100,
                            showEditMode: true,
                            defaultAnswersTo: {
                                highestScore: false,
                                lowestScore: false,
                                notApplicable: false,
                                userDefined: false,
                            },
                        },
                    ],
                    evaluationSettings: {
                        revisionsEnabled: true,
                        disputesEnabled: false,
                        disputesAllowedPerEval:
                            DefaultLimits.defaultDisputesPerEvaluation,
                        disputesAssignees: Array.from(
                            {
                                length: DefaultLimits.defaultDisputesPerEvaluation,
                            },
                            () => ({
                                type: EvaluationSettingsAssigneeTypeEnum.Original,
                            })
                        ),
                    },
                    aiScoring: {
                        questionGroupSettings: [
                            {
                                questionGroupContextId: "",
                                questionSettings: [],
                            },
                        ],
                    },
                    evaluationFormType:
                        "evaluationFormType" in rest
                            ? rest.evaluationFormType
                            : null,
                } as Omit<EvaluationFormResponse, "questionGroups"> & {
                    questionGroups: Array<
                        EvaluationQuestionGroup & { showEditMode?: boolean }
                    >;
                };
            }
            try {
                const parsedForm = await qualityApi.getQualityFormsEvaluation({
                    formId,
                });

                // Division is already included in the parsed form from SDK
                return parsedForm;
            } catch (error: any) {
                return rejectWithValue({
                    status: error?.status,
                    formType,
                    formId,
                    titleKey: "composer.evaluations.evaluation.notFound.title",
                    messageKey:
                        "composer.evaluations.evaluation.notFound.message",
                });
            }
        } else if (formType === FormType.SURVEY) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            if (formId === "new") {
                return {
                    id: "new",
                    contextId: uuidv4(),
                    name: "",
                    questionGroups: [
                        {
                            id: uuidv4(),
                            name: "",
                            type: "questionGroup",
                            questions: [],
                            showEditMode: true,
                        },
                    ],
                    language: "language" in rest ? rest.language : "en-US",
                    showEditMode: true,
                } as Omit<SurveyForm, "questionGroups"> & {
                    questionGroups: Array<
                        SurveyQuestionGroup & { showEditMode?: boolean }
                    >;
                };
            }
            try {
                const parsedForm = await qualityApi.getQualityFormsSurvey({
                    formId,
                });

                // Division is already included in the parsed form from SDK
                return parsedForm;
            } catch (error: any) {
                return rejectWithValue({
                    status: error?.status,
                    formType,
                    formId,
                    titleKey: "composer.surveys.editor.notFound.title",
                    messageKey: "composer.surveys.editor.notFound.message",
                });
            }
        } else if (formType === FormType.ASSESSMENT) {
            const newAssessmentForm = {
                id: "new",
                name: "",
                passPercent: 80,
                questionGroups: [
                    {
                        id: uuidv4(),
                        name: "",
                        type: "questionGroup",
                        questions: [],
                        showEditMode: true,
                        weight: 100,
                    },
                ],
            } as Omit<AssessmentForm, "questionGroups"> & {
                questionGroups: Array<
                    AssessmentFormQuestionGroup & { showEditMode?: boolean }
                >;
            };
            if ("assessmentForm" in rest && rest.assessmentForm) {
                return { ...rest.assessmentForm, isArchived: rest.isArchived };
            } else {
                return newAssessmentForm;
            }
        } else {
            throw new Error(`Cannot load formType: ${formType}`);
        }
    }
);

function getFormIdNameTuples(
    forms?: EvaluationFormResponse[] | SurveyForm[]
): FormNames[] {
    return forms
        ? forms.map((form) => {
              return { contextId: form.contextId, name: form.name };
          })
        : [];
}

export const loadFormNames = createAsyncThunk(
    "qualityForm/loadFormNames",
    async (
        formType: FormType.EVALUATION | FormType.SURVEY
    ): Promise<FormNames[]> => {
        const qualityApi = new QualityApi(API_CONFIGURATION);
        const getForms = (requestData: {
            pageSize: number;
            pageNumber: number;
        }) => {
            if (formType === FormType.EVALUATION) {
                return qualityApi.getQualityFormsEvaluations(requestData);
            }
            if (formType === FormType.SURVEY) {
                return qualityApi.getQualityFormsSurveys(requestData);
            }
            return Promise.reject("Invalid form type.");
        };

        const requestData = {
            pageSize: 100,
            pageNumber: 1,
        };
        const { pageCount, entities } = await getForms(requestData);
        const firstPageResults = getFormIdNameTuples(entities);

        if (pageCount && pageCount > 1) {
            const pagePromises = range(2, pageCount + 1).map((pageNumber) => {
                requestData.pageNumber = pageNumber;
                return getForms(requestData);
            });
            const pageResults = await Promise.all(pagePromises);
            const tailResults = pageResults.flatMap(({ entities }) =>
                getFormIdNameTuples(entities)
            );
            return [...firstPageResults, ...tailResults];
        }

        return firstPageResults;
    }
);

export const evaluatorNoPermissionErrorHandler = (
    state: RootState,
    dispatch: Dispatch,
    error: any
) => {
    const { formId, evaluatorId }: Record<any, string> = error.messageParams;
    state.qualityForm.data.evaluationSettings.disputeAssignments.forEach(
        (assignee, i) => {
            if (
                assignee.type ===
                    EvaluationSettingsAssigneeTypeEnum.Individual &&
                assignee.assigneeUser?.value === evaluatorId
            ) {
                dispatch(
                    qualityFormActions.setAssigneeUserError({
                        index: i,
                        error: true,
                    })
                );
            }
        }
    );
    return formId;
};

// create a thunk for saving AI Scoring Settings
export const saveAiScoringSettings = createAsyncThunk<
    AiScoringSettings,
    undefined,
    { state: RootState }
>("qualityForm/saveAiScoringSettings", async (arg, { getState }) => {
    const state = getState();
    const qualityApi = new QualityApi(API_CONFIGURATION);
    const formType = selectFormType(state);

    const isAiScoringFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.AI_SCORING
    );
    const userHasAiScoringEditPermission = selectPermission(
        state,
        QualityFormPermission.QUALITY_EVAL_FORM_AI_SCORING_EDIT
    );
    let formattedAiScoringSettings: AiScoringSettings;
    if (
        isAiScoringFTEnabled &&
        formType === FormType.EVALUATION &&
        userHasAiScoringEditPermission
    ) {
        const questions = state.qualityForm.data.questions;

        formattedAiScoringSettings = toFormAiScoringSettings(
            state.qualityForm.data.aiScoring,
            state.qualityForm.data.questionGroups,
            questions
        );
        const formId = state.qualityForm.data.form?.id;
        if (formId && formattedAiScoringSettings) {
            return qualityApi
                .putQualityFormsEvaluationAiscoringSettings({
                    formId,
                    body: formattedAiScoringSettings as AiScoringSettings,
                })
                .catch((err) => {
                    throw err;
                });
        }
    }

    throw new Error("Saving AI Scoring configurations was not processed");
});

export const saveForm = createAsyncThunk<
    EvaluationFormResponse | SurveyForm | AiScoringSettingsErrorPayload,
    undefined,
    { state: RootState }
>(
    "qualityForm/saveForm",
    async (arg, { getState, rejectWithValue, dispatch }) => {
        const state = getState();
        const qualityApi = new QualityApi(API_CONFIGURATION);
        const formType = selectFormType(state);
        const denormalizedForm = getDenormalizedQualityForm(state);
        if (denormalizedForm && denormalizedForm.id) {
            if (!selectIsFormValid(state)) {
                return rejectWithValue(
                    new Error("Form was not saved due to validation error")
                );
            }

            if (formType === FormType.EVALUATION) {
                const evaluationSettings = (denormalizedForm as EvaluationForm)
                    .evaluationSettings;
                if (
                    evaluationSettings &&
                    evaluationSettings.disputesEnabled &&
                    evaluationSettings.disputesAssignees
                ) {
                    let invalidUsers = evaluationSettings.disputesAssignees.map(
                        (assignee) =>
                            assignee.type ===
                                EvaluationSettingsAssigneeTypeEnum.Individual &&
                            !assignee.user?.id
                                ? assignee
                                : undefined
                    );
                    invalidUsers = invalidUsers.flatMap((user, i) => {
                        if (user) {
                            dispatch(
                                qualityFormActions.setAssigneeUserError({
                                    index: i,
                                    error: true,
                                })
                            );
                            return [user];
                        }
                        return [];
                    });
                    if (invalidUsers?.length > 0) {
                        return rejectWithValue(
                            new Error(
                                "Form was not saved due to validation error"
                            )
                        );
                    }
                }

                // convert the aiScoring field to the Form-friendly version
                const isAiScoringFTEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.AI_SCORING
                );
                const userHasAiScoringEditPermission = selectPermission(
                    state,
                    QualityFormPermission.QUALITY_EVAL_FORM_AI_SCORING_EDIT
                );
                (denormalizedForm as EvaluationForm).aiScoring = undefined;

                let indexAiSettings: AiScoringSettings | undefined;

                if (isAiScoringFTEnabled) {
                    indexAiSettings = toIndexAiScoringSettings(
                        state.qualityForm.data.aiScoring,
                        state.qualityForm.data.form?.questionGroups,
                        state.qualityForm.data.questionGroups
                    );
                } else {
                    indexAiSettings = undefined;
                }

                if (denormalizedForm.id === "new") {
                    return qualityApi
                        .postQualityFormsEvaluations({
                            body: denormalizedForm as EvaluationForm,
                        })
                        .then(
                            (
                                evaluationFormResponse: EvaluationFormResponse
                            ) => {
                                let formattedAiScoringSettings:
                                    | AiScoringSettings
                                    | undefined;
                                if (isAiScoringFTEnabled && indexAiSettings) {
                                    formattedAiScoringSettings =
                                        toContextIdAiScoringSettings(
                                            indexAiSettings,
                                            evaluationFormResponse.questionGroups
                                        );
                                } else {
                                    formattedAiScoringSettings = undefined;
                                }

                                // make PUT request for AI Scoring configurations if
                                // 1. user has editPermission and
                                // 2. at least one question is configured with AI Scoring...
                                // 3. AI SCORING FT is enabled
                                if (
                                    evaluationFormResponse.id &&
                                    isAiScoringFTEnabled &&
                                    formattedAiScoringSettings?.questionGroupSettings &&
                                    formattedAiScoringSettings
                                        .questionGroupSettings.length > 0 &&
                                    userHasAiScoringEditPermission
                                ) {
                                    return qualityApi
                                        .putQualityFormsEvaluationAiscoringSettings(
                                            {
                                                formId: evaluationFormResponse.id,
                                                body: formattedAiScoringSettings as AiScoringSettings,
                                            }
                                        )
                                        .then((aiScoringResponse) => {
                                            // replace aiScoring with the newly returned one
                                            evaluationFormResponse.aiScoring =
                                                aiScoringResponse;
                                            return evaluationFormResponse;
                                        })
                                        .catch((err) => {
                                            console.log(
                                                "Failed to save AI Scoring Configuration",
                                                err
                                            );

                                            return {
                                                aiScoringSettingsError: true,
                                                isAiScoringEditPermissionError:
                                                    err["status"] === 403,
                                                aiScoringSettings:
                                                    formattedAiScoringSettings as AiScoringSettings,
                                                evaluationForm:
                                                    evaluationFormResponse,
                                            };
                                        });
                                }
                                return evaluationFormResponse;
                            }
                        )
                        .catch(async (err: Response) => {
                            const response = await err.json();
                            if (
                                response.status === 400 &&
                                response.code ===
                                    "quality.evaluation.config.evaluator.no.permission"
                            ) {
                                evaluatorNoPermissionErrorHandler(
                                    state,
                                    dispatch,
                                    response
                                );
                                return {
                                    settingsError: true,
                                };
                            }
                            throw err;
                        });
                } else {
                    // return qualityApi
                    //     .putQualityFormsEvaluation({
                    //         formId: denormalizedForm.id,
                    //         body: denormalizedForm as EvaluationForm,
                    //     })

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    return apiCore.v2
                        .put(
                            "/quality/forms/evaluations/" + denormalizedForm.id,
                            denormalizedForm as EvaluationForm
                        )
                        .then((evaluationFormResponse: any) => {
                            if (evaluationFormResponse?.modifiedDate) {
                                evaluationFormResponse.modifiedDate = new Date(
                                    evaluationFormResponse.modifiedDate
                                );
                            }
                            let formattedAiScoringSettings:
                                | AiScoringSettings
                                | undefined;
                            if (isAiScoringFTEnabled && indexAiSettings) {
                                formattedAiScoringSettings =
                                    toContextIdAiScoringSettings(
                                        indexAiSettings,
                                        evaluationFormResponse.questionGroups
                                    );
                            } else {
                                formattedAiScoringSettings = undefined;
                            }
                            // make PUT request for AI Scoring configurations if
                            // 1. user has editPermission and
                            // 2. aiScoring is defined
                            // 3. AI SCORING FT is enabled
                            if (
                                evaluationFormResponse.id &&
                                isAiScoringFTEnabled &&
                                formattedAiScoringSettings &&
                                userHasAiScoringEditPermission
                            ) {
                                return qualityApi
                                    .putQualityFormsEvaluationAiscoringSettings(
                                        {
                                            formId: evaluationFormResponse.id,
                                            body: formattedAiScoringSettings as AiScoringSettings,
                                        }
                                    )
                                    .then((aiScoringResponse) => {
                                        // replace aiScoring with the newly returned one
                                        evaluationFormResponse.aiScoring =
                                            aiScoringResponse;
                                        return evaluationFormResponse;
                                    })
                                    .catch((err) => {
                                        console.log(
                                            "Failed to save AI Scoring Configuration",
                                            err
                                        );

                                        return {
                                            aiScoringSettingsError: true,
                                            isAiScoringEditPermissionError:
                                                err["status"] === 403,
                                            aiScoringSettings:
                                                formattedAiScoringSettings as AiScoringSettings,
                                            evaluationForm:
                                                evaluationFormResponse,
                                        };
                                    });
                            }

                            return evaluationFormResponse;
                        })
                        .catch((err: any) => {
                            // Throws HttpError
                            const error = err?.details?.response;
                            if (
                                error !== undefined &&
                                error?.code === "bad.request" &&
                                error?.messageParams?.topicIds?.length > 0
                            ) {
                                dispatch(
                                    topicSliceActions.addRejectedTopicIds(
                                        error.messageParams.topicIds
                                    )
                                );
                            }
                            if (
                                error &&
                                error.code ===
                                    "quality.evaluation.config.evaluator.no.permission"
                            ) {
                                evaluatorNoPermissionErrorHandler(
                                    state,
                                    dispatch,
                                    error
                                );
                                return {
                                    settingsError: true,
                                };
                            }
                            throw new Error("Form was not saved");
                        });
                }
            } else if (formType === FormType.SURVEY) {
                if (denormalizedForm.id === "new") {
                    return qualityApi.postQualityFormsSurveys({
                        body: denormalizedForm as SurveyForm,
                    });
                } else {
                    return qualityApi.putQualityFormsSurvey({
                        formId: denormalizedForm.id,
                        body: denormalizedForm as SurveyForm,
                    });
                }
            }
        }
        throw new Error("Form was not saved");
    }
);

export const disableSurveyForm = createAsyncThunk<
    SurveyForm,
    undefined,
    { state: RootState }
>("qualityForm/disableSurveyForm", async (arg, { getState }) => {
    const state = getState();
    const qualityApi = new QualityApi(API_CONFIGURATION);
    const denormalizedForm = getDenormalizedQualityForm(state) as SurveyForm;
    if (denormalizedForm && denormalizedForm.id) {
        return qualityApi.patchQualityFormsSurvey({
            formId: denormalizedForm.id,
            body: { ...denormalizedForm, disabled: true },
        });
    }
    throw new Error("Form was not disabled");
});

export const publishForm = createAsyncThunk<
    EvaluationFormResponse | SurveyForm | EvaluationSettingsErrorPayload,
    undefined,
    { state: RootState }
>("qualityForm/publishForm", async (arg, { getState, dispatch }) => {
    const state = getState();
    const form = selectForm(state);
    const formType = selectFormType(state);
    const qualityApi = new QualityApi(API_CONFIGURATION);
    if (form?.id && form.published !== undefined) {
        if (formType === FormType.EVALUATION) {
            return qualityApi
                .postQualityPublishedformsEvaluations({
                    body: {
                        id: form.id,
                        published: form.published,
                    },
                })
                .catch(async (err: Response) => {
                    const response = await err.json();
                    if (
                        response.status === 400 &&
                        response.code ===
                            "quality.evaluation.config.evaluator.no.permission"
                    ) {
                        const formId = evaluatorNoPermissionErrorHandler(
                            state,
                            dispatch,
                            response
                        );
                        return {
                            id: formId,
                            settingsError: true,
                            evaluationSettings:
                                getState().qualityForm.data.evaluationSettings,
                        };
                    }
                    throw err;
                });
        } else if (formType === FormType.SURVEY) {
            return qualityApi.postQualityPublishedformsSurveys({
                body: {
                    id: form.id,
                    published: form.published,
                },
            });
        }
    }

    throw new Error("Form  not published");
});

export const loadAgreementRate = createAsyncThunk<
    {
        form: AgreementRateData;
        questions: QuestionAgreementRates;
        latestPublishedFormId?: string;
    },
    { currentFormId: string; questionIds?: string[] },
    { state: RootState }
>(
    "qualityForm/loadAgreementRate",
    async ({ currentFormId, questionIds }, { getState, rejectWithValue }) => {
        try {
            const state = getState();

            // Get questions from state for question-level agreement rates
            const questions = state.qualityForm.data.questions;
            if (!questions) {
                return rejectWithValue(
                    new Error("Agreement Rate was not loaded")
                );
            }

            const latestPublishedForm = await getLatestPublishedForm(
                currentFormId
            );
            const latestPublishedFormId = latestPublishedForm?.id ?? "";

            const isDraftFormAgreementRateFeatureEnabled = selectFeatureToggle(
                state,
                FeatureToggle.DRAFT_FORM_AGREEMENT_RATE
            );
            const isDraftForm =
                isDraftFormAgreementRateFeatureEnabled &&
                !state.qualityForm.data.form?.published;

            // If it is the latest published form or a draft form by WEM-1579 FT (with a published version)
            // then we're viewing the agreement rate data for the latest published version
            const shouldLoadFormAgreementRate =
                latestPublishedFormId === currentFormId ||
                (isDraftForm && !!latestPublishedFormId);

            if (shouldLoadFormAgreementRate) {
                // Draft form: only load form-level agreement rate (question rates are not shown in edit mode)
                if (isDraftForm) {
                    const formResponse = await getFormAgreementRate(
                        latestPublishedFormId
                    );
                    if (!formResponse.success || !formResponse.data) {
                        return rejectWithValue(
                            new Error(
                                "Failed to fetch form agreement rate data"
                            )
                        );
                    }
                    const emptyQuestions: QuestionAgreementRates = {};
                    if (questionIds) {
                        questionIds.forEach((questionId) => {
                            emptyQuestions[questionId] =
                                createEmptyAgreementRateData();
                        });
                    }
                    return {
                        form: formResponse.data,
                        questions: emptyQuestions,
                        latestPublishedFormId: latestPublishedFormId,
                    };
                }

                // Published form: load both form and question agreement rates in parallel
                const [formResponse, questionResponse] = await Promise.all([
                    getFormAgreementRate(latestPublishedFormId),
                    getQuestionAgreementRates(
                        latestPublishedFormId,
                        questions,
                        {
                            questionIds,
                        }
                    ),
                ]);

                if (!formResponse.success || !formResponse.data) {
                    return rejectWithValue(
                        new Error("Failed to fetch form agreement rate data")
                    );
                }

                if (!questionResponse.success || !questionResponse.data) {
                    return rejectWithValue(
                        new Error(
                            "Failed to fetch question agreement rate data"
                        )
                    );
                }

                return {
                    form: formResponse.data,
                    questions: questionResponse.data,
                    latestPublishedFormId: latestPublishedFormId,
                };
            }

            // If no published response ID, return empty data structure
            const emptyForm = createEmptyAgreementRateData();
            const emptyQuestions: QuestionAgreementRates = {};
            if (questionIds) {
                questionIds.forEach((questionId) => {
                    emptyQuestions[questionId] = createEmptyAgreementRateData();
                });
            }

            return {
                form: emptyForm,
                questions: emptyQuestions,
                latestPublishedFormId: undefined,
            };
        } catch (error) {
            console.error("Error loading form agreement rate:", error);
            throw new Error(
                "An unexpected error occurred while loading agreement rate data"
            );
        }
    }
);

export const sendAssessmentFormData =
    () => (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        if (
            state.qualityForm.data.formType === FormType.ASSESSMENT &&
            selectIsFormValid(state)
        ) {
            const denormalizedForm = getDenormalizedQualityForm(
                state
            ) as AssessmentForm;
            if (denormalizedForm && denormalizedForm.id) {
                const data = {
                    formData: {
                        ...denormalizedForm,
                        id: denormalizedForm.id,
                    },
                    isValid: selectIsFormValid(state),
                    isModified: selectIsModified(state),
                };
                return embeddingService.sendFormData(data);
            }
            throw new Error("Could not denormalize form data.");
        }
        const data = {
            isValid: false,
            isModified: selectIsModified(state),
        };
        return embeddingService.sendFormData(data);
    };

export const initiateAnswerOptionDeletion =
    (questionId: string, answerOptionId: string, isMultipleSelect?: boolean) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        // Converting undefined to boolean false
        isMultipleSelect = !!isMultipleSelect;
        const state = getState();
        const visibilityConditionDependents =
            selectDependentVisibilityConditionForEntity(
                state.qualityForm,
                answerOptionId,
                EntityType.Answer,
                isMultipleSelect
            );
        if (
            visibilityConditionDependents.questionGroupIds.length !== 0 ||
            visibilityConditionDependents.questionIds.length !== 0
        ) {
            return visibilityConditionDependents;
        }
        // dispatching delete answer when there is no visibility condition conflict.
        dispatch(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            qualityFormActions.deleteAnswerOption({
                questionId,
                answerOptionId,
                isMultipleSelect,
            })
        );
    };

export const initiateQuestionDeletion =
    (questionId: string, isMultipleSelect?: boolean) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const visibilityConditionDependents =
            selectDependentVisibilityConditionForEntity(
                state.qualityForm,
                questionId,
                EntityType.Question,
                !!isMultipleSelect
            );
        if (
            visibilityConditionDependents.questionGroupIds.length !== 0 ||
            visibilityConditionDependents.questionIds.length !== 0
        ) {
            return visibilityConditionDependents;
        }
        // dispatching delete answer when there is no visibility condition conflict.
        dispatch(qualityFormActions.deleteQuestion(questionId));
    };

export const initiateQuestionGroupDeletion =
    (questionGroupId: string) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const visibilityConditionDependents =
            selectDependentVisibilityConditionForEntity(
                state.qualityForm,
                questionGroupId,
                EntityType.QuestionGroup,
                false
            );
        if (
            visibilityConditionDependents.questionGroupIds.length !== 0 ||
            visibilityConditionDependents.questionIds.length !== 0
        ) {
            return visibilityConditionDependents;
        }
        // dispatching delete answer when there is no visibility condition conflict.
        dispatch(qualityFormActions.deleteQuestionGroup(questionGroupId));
    };

export const moveQuestionUpIfValid =
    (questionId: string) => (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const questionGroupId = selectQuestionGroupIdContainingQuestion(
            questionId
        )(state.qualityForm)!;
        const questionsWithinTheSameGroup =
            state.qualityForm.data?.questionGroups?.[questionGroupId]
                .questions ?? [];
        const indexOfSelectedQuestionWithinQuestionGroup =
            questionsWithinTheSameGroup?.indexOf(questionId);

        if (indexOfSelectedQuestionWithinQuestionGroup < 1) {
            return false;
        }

        const dependentEntities: VisibilityConflictsOnMove = {
            questionIds: [],
        };
        if (
            !selectIsQuestionValidToMoveUpWithVisibilityCondition(
                questionsWithinTheSameGroup[
                    indexOfSelectedQuestionWithinQuestionGroup - 1
                ],
                questionsWithinTheSameGroup[
                    indexOfSelectedQuestionWithinQuestionGroup
                ]
            )(state.qualityForm)
        ) {
            dependentEntities.questionIds.push(
                questionsWithinTheSameGroup[
                    indexOfSelectedQuestionWithinQuestionGroup - 1
                ]
            );
            return dependentEntities;
        }
        dispatch(
            qualityFormActions.moveQuestion({
                questionId,
                newRelativePosition: -1,
                questionGroupId,
                indexOfSelectedQuestionWithinQuestionGroup,
            })
        );
    };

export const moveQuestionDownIfValid =
    (questionId: string) => (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const questionGroupId = selectQuestionGroupIdContainingQuestion(
            questionId
        )(state.qualityForm)!;

        const questionsWithinTheSameGroup =
            state.qualityForm.data?.questionGroups?.[questionGroupId]
                .questions ?? [];
        const indexOfSelectedQuestionWithinQuestionGroup =
            questionsWithinTheSameGroup?.indexOf(questionId);

        if (
            indexOfSelectedQuestionWithinQuestionGroup >=
            questionsWithinTheSameGroup.length - 1
        ) {
            return false;
        }

        const dependentEntities: VisibilityConflictsOnMove = {
            questionIds: [],
        };
        if (
            !selectIsQuestionValidToMoveUpWithVisibilityCondition(
                questionsWithinTheSameGroup[
                    indexOfSelectedQuestionWithinQuestionGroup
                ],
                questionsWithinTheSameGroup[
                    indexOfSelectedQuestionWithinQuestionGroup + 1
                ]
            )(state.qualityForm)
        ) {
            dependentEntities.questionIds.push(
                questionsWithinTheSameGroup[
                    indexOfSelectedQuestionWithinQuestionGroup + 1
                ]
            );
            return dependentEntities;
        }

        dispatch(
            qualityFormActions.moveQuestion({
                questionId,
                newRelativePosition: +1,
                questionGroupId,
                indexOfSelectedQuestionWithinQuestionGroup,
            })
        );
    };

export const moveQuestionGroupUpIfValid =
    (questionGroupId: string) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const questionGroups = state.qualityForm.data.form?.questionGroups;
        const indexOfSelectedQuestionGroup =
            questionGroups?.indexOf(questionGroupId) ?? 0;
        if (!questionGroups || indexOfSelectedQuestionGroup < 1) {
            return false;
        }

        const visibilityConflictsOnMove =
            selectVisibilityDependentsForQuestionGroupToMoveUp(
                questionGroups[indexOfSelectedQuestionGroup - 1],
                questionGroups[indexOfSelectedQuestionGroup]
            )(state.qualityForm);
        if (
            visibilityConflictsOnMove.questionIds.length !== 0 ||
            Object.keys(
                visibilityConflictsOnMove.conflictingQuestionsWithinQuestionGroup ??
                    {}
            ).length !== 0
        ) {
            return visibilityConflictsOnMove;
        }
        dispatch(
            qualityFormActions.moveQuestionGroup({
                questionGroupId,
                newRelativePosition: -1,
                indexOfSelectedQuestionGroup,
            })
        );
    };

export const moveQuestionGroupDownIfValid =
    (questionGroupId: string) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const questionGroups = state.qualityForm.data.form?.questionGroups;
        const indexOfSelectedQuestionGroup =
            questionGroups?.indexOf(questionGroupId) ?? Number.MAX_VALUE;
        if (
            !questionGroups ||
            indexOfSelectedQuestionGroup >= questionGroups.length - 1
        ) {
            return false;
        }

        const visibilityConflictsOnMove =
            selectVisibilityDependentsForQuestionGroupToMoveUp(
                questionGroups[indexOfSelectedQuestionGroup],
                questionGroups[indexOfSelectedQuestionGroup + 1]
            )(state.qualityForm);
        if (
            visibilityConflictsOnMove.questionIds.length !== 0 ||
            Object.keys(
                visibilityConflictsOnMove.conflictingQuestionsWithinQuestionGroup ??
                    {}
            ).length !== 0
        ) {
            return visibilityConflictsOnMove;
        }
        dispatch(
            qualityFormActions.moveQuestionGroup({
                questionGroupId,
                newRelativePosition: +1,
                indexOfSelectedQuestionGroup,
            })
        );
    };

export const addQuestionFromSavedTemplate =
    (questionGroupId: string, templateId: string) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        // exclude question id and visibility condition for existing template
        const { id, visibilityCondition, ...template } = selectTemplate(
            state,
            templateId
        )?.data as any;
        const isSurveyFreeTextLimitEnabled = selectFeatureToggle(
            state,
            FeatureToggle.VOICE_SURVEY_FREE_TEXT_CHAR_LIMIT_INCREASE
        );
        dispatch(
            qualityFormActions.addQuestionToQuestionGroup({
                questionGroupId,
                template: template as TemplateQuestion,
                showEditMode: false,
                isSurveyFreeTextLimitEnabled,
            })
        );
    };

// (question/questionGroup).visibilityCondition is set undefined when the question/questionGroup unckecks "display conditionally" .
export const toggleConditionalDisplay =
    (
        id: string,
        formEntity: "question" | "questionGroup",
        isMultipleSelectFTEnabled: boolean
    ) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const presentToggleState =
            (formEntity === "question"
                ? state.qualityForm.data.questions?.[id]
                : state.qualityForm.data.questionGroups?.[id]
            )?.visibilityCondition !== undefined;

        if (presentToggleState) {
            // toggling off (unchecking "Display conditionally")
            dispatch(
                qualityFormActions.toggleConditionalDisplay({
                    id: id,
                    formEntity: formEntity,
                })
            );
        } else {
            const defaultQuestionId =
                (formEntity === "question"
                    ? selectEligibleQuestionsForVisibilityConditionsForQuestion(
                          state,
                          id,
                          false
                      )?.[0]
                    : selectEligibleQuestionsForVisibilityConditionsForQuestionGroup(
                          state,
                          id,
                          false
                      )?.[0]) ?? "";
            const defaultQuestionGroupId =
                selectQuestionGroupIdContainingQuestion(defaultQuestionId)(
                    state.qualityForm
                ) ?? "";

            const defaultAnswerId =
                (formEntity === "question"
                    ? selectEligibleAnswersForVisibilityConditionInQuestion(
                          state.qualityForm,
                          defaultQuestionId,
                          id,
                          isMultipleSelectFTEnabled
                      )?.[0]
                    : selectEligibleAnswersForVisibilityConditionInQuestionGroup(
                          state.qualityForm,
                          defaultQuestionId,
                          id,
                          isMultipleSelectFTEnabled
                      )?.[0]) ?? "";

            dispatch(
                qualityFormActions.toggleConditionalDisplay({
                    id: id,
                    formEntity: formEntity,
                    defaultQuestionId,
                    defaultAnswerId,
                    defaultQuestionGroupId,
                })
            );
        }
    };

export const updateAssistanceConditionsWithValidTopics =
    (
        answerId: string,
        isMultipleSelect: boolean,
        index: number,
        selectedTopics?: string[]
    ) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        if (selectedTopics) {
            const deletedTopicIds = selectUnpublishedTopics(
                getState(),
                selectedTopics
            ).map((topic) => topic.id);
            if (deletedTopicIds.length > 0)
                selectedTopics = selectedTopics.filter(
                    (topicId) => !deletedTopicIds.includes(topicId)
                );
        }
        dispatch(
            qualityFormActions.updateAssistanceConditionTopics({
                answerId,
                isMultipleSelect,
                index,
                selectedTopics,
            })
        );
    };

// TODO: add the new slice for ai scoring configuration
const initialState: QualityFormState = {
    data: {
        isModified: false,
        evaluationSettings: {
            disputesEnabled: false,
            revisionsEnabled: false,
            disputesAllowedPerEvaluation:
                DefaultLimits.defaultDisputesPerEvaluation,
            disputeAssignments: Array.from(
                {
                    length: DefaultLimits.defaultDisputesPerEvaluation,
                },
                () => ({
                    type: EvaluationSettingsAssigneeTypeEnum.Original,
                })
            ),
        },
        aiScoring: {
            questionGroupSettings: {},
        },
        screenReaderAnnouncement: "",
    },
};

export const slice = createSlice({
    name: "qualityForm",
    initialState,
    reducers: {
        copyForm(
            state,
            {
                payload,
            }: PayloadAction<{
                newFormName: string;
            }>
        ) {
            const formData = state.data;
            if (formData) {
                const form = formData.form;
                const formType = formData.formType;
                if (
                    formType === FormType.EVALUATION ||
                    formType === FormType.SURVEY
                ) {
                    formData.isModified = true;
                    if (form) {
                        form.id = "new";
                        form.contextId = undefined;
                        form.name = payload.newFormName;
                        form.published = undefined;
                        form.dateModified = undefined;
                        form.selfUri = undefined;
                        form.disabled = undefined;
                        form.isArchived = undefined;
                    }
                }
            }
        },
        setQuestionGroupWeight(
            state,
            { payload }: { payload: { id: string; weight: number } }
        ) {
            // not updating weight to a negative number.
            if (isNonNegativeNumber(payload.weight, true)) {
                const questionGroup = state.data.questionGroups?.[payload.id];
                if (questionGroup) {
                    questionGroup.weight = calculateGroupWeight(payload.weight);
                    questionGroup.manualWeight = true;
                }
            }
        },
        setQuestionGroupName(
            state,
            { payload }: { payload: { groupId: string; newName: string } }
        ) {
            const questionGroup = state.data.questionGroups?.[payload.groupId];
            if (questionGroup) {
                questionGroup.name = payload.newName;
            }
        },
        toggleQuestionGroupNAEnabled(
            state,
            { payload }: { payload: { groupId: string } }
        ) {
            const questionGroup = state.data.questionGroups?.[payload.groupId];
            if (questionGroup) {
                questionGroup.naEnabled = !questionGroup.naEnabled;
            }
        },
        toggleDefaultAnswersToHighest(
            state,
            {
                payload,
            }: {
                payload: {
                    groupId: string;
                    isEvaluationFormDefaultAnsweringEnabled: boolean;
                };
            }
        ) {
            const questionGroup = state.data.questionGroups?.[payload.groupId];
            if (questionGroup) {
                if (payload.isEvaluationFormDefaultAnsweringEnabled) {
                    const defaultAnswersTo =
                        getInitializedDefaultAnswersTo(questionGroup);
                    defaultAnswersTo.highestScore =
                        !defaultAnswersTo.highestScore;

                    if (!questionGroup.defaultAnswersTo?.notApplicable) {
                        disableNAForQuestions(state, questionGroup.questions);
                    }
                }

                questionGroup.defaultAnswersToHighest =
                    !questionGroup.defaultAnswersToHighest;
            }
        },
        toggleDefaultAnswersToLowest(
            state,
            { payload }: { payload: { groupId: string } }
        ) {
            const questionGroup = state.data.questionGroups?.[payload.groupId];
            if (questionGroup) {
                const defaultAnswersTo =
                    getInitializedDefaultAnswersTo(questionGroup);
                defaultAnswersTo.lowestScore = !defaultAnswersTo.lowestScore;

                disableNAForQuestions(state, questionGroup.questions);
            }
        },
        toggleDefaultAnswersToUserDefined(
            state,
            { payload }: { payload: { groupId: string } }
        ) {
            const questionGroup = state.data.questionGroups?.[payload.groupId];
            if (questionGroup) {
                const defaultAnswersTo =
                    getInitializedDefaultAnswersTo(questionGroup);
                defaultAnswersTo.userDefined = !defaultAnswersTo.userDefined;

                // clear all current default answers for the question group
                const questionIds = questionGroup.questions;
                const isAutomatedEvaluationForm =
                    selectIsAutomatedEvaluationForm({
                        qualityForm: state,
                    } as RootState);

                questionIds?.forEach((id) => {
                    const question = state.data.questions?.[id];
                    if (question) {
                        question.defaultAnswer = undefined;
                        if (isAutomatedEvaluationForm) {
                            question.naEnabled = false;
                        }
                    }
                    // Also clear default answers for multiple select questions
                    question?.multipleSelectOptionQuestions?.forEach(
                        (msqId) => {
                            const msQuestion =
                                state.data.multipleSelectOptionQuestions?.[
                                    msqId
                                ];
                            if (msQuestion) {
                                msQuestion.defaultAnswer = undefined;
                            }
                        }
                    );
                });
            }
        },
        toggleDefaultAnswersToNA(
            state,
            {
                payload,
            }: {
                payload: {
                    groupId: string;
                    isEvaluationFormDefaultAnsweringEnabled: boolean;
                };
            }
        ) {
            const questionGroup = state.data.questionGroups?.[payload.groupId];
            const formType = state.data.formType;
            if (questionGroup) {
                if (
                    payload.isEvaluationFormDefaultAnsweringEnabled &&
                    formType === FormType.EVALUATION
                ) {
                    const defaultAnswersTo =
                        getInitializedDefaultAnswersTo(questionGroup);
                    defaultAnswersTo.notApplicable =
                        !defaultAnswersTo.notApplicable;

                    if (!defaultAnswersTo.notApplicable) {
                        disableNAForQuestions(state, questionGroup.questions);
                    }
                }
                questionGroup.defaultAnswersToNA =
                    !questionGroup.defaultAnswersToNA;
            }
        },
        // Sets the default answer for a question or multiple select question option when user defined is selected
        // For multiple select questions, handles setting default answer on the multiple select option level
        // For regular questions, sets the default answer directly on the question
        // If the same option is selected again, it will clear/unset the default answer
        setDefaultAnswer(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string; // ID of the question to set default answer for
                optionId: string; // ID of the answer option to set as default
                isMultipleSelect: boolean; // Whether this is a multiple select question
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                if (payload.isMultipleSelect) {
                    // For multiple select questions, get the option ID with the builtInType of 'Selected'
                    const selectedAnswerOption = selectSelectedOptionIdForMSQ(
                        { qualityForm: state } as RootState,
                        payload.optionId
                    );

                    // Get the multiple select question option
                    const multipleSelectQuestion =
                        state.data.multipleSelectOptionQuestions?.[
                            payload.optionId
                        ];
                    if (multipleSelectQuestion) {
                        // If no default answer exists, create one
                        if (!multipleSelectQuestion.defaultAnswer) {
                            multipleSelectQuestion.defaultAnswer = {
                                id: selectedAnswerOption,
                            };
                            // If same option selected again, clear the default answer
                        } else if (
                            selectedAnswerOption ===
                            multipleSelectQuestion.defaultAnswer.id
                        ) {
                            multipleSelectQuestion.defaultAnswer = undefined;
                            // Otherwise update the default answer ID
                        } else {
                            multipleSelectQuestion.defaultAnswer.id =
                                selectedAnswerOption;
                        }
                    }
                } else {
                    // For regular questions, if no default answer exists, create one
                    if (!question.defaultAnswer) {
                        question.defaultAnswer = {
                            id: payload.optionId,
                        };
                        // If same option selected again, clear the default answer
                    } else if (payload.optionId === question.defaultAnswer.id) {
                        question.defaultAnswer = undefined;
                        // Otherwise update the default answer ID
                    } else {
                        question.defaultAnswer.id = payload.optionId;
                    }
                }
            }
        },
        toggleDisputes(state) {
            if (state.data.evaluationSettings) {
                state.data.evaluationSettings.disputesEnabled =
                    !state.data.evaluationSettings.disputesEnabled;
            }
        },
        toggleRevisions(state) {
            if (state.data.evaluationSettings) {
                state.data.evaluationSettings.revisionsEnabled =
                    !state.data.evaluationSettings.revisionsEnabled;
            }
        },
        setDisputesAllowedPerEvaluation(
            state,
            {
                payload,
            }: PayloadAction<{
                disputesAllowedPerEvaluation: number;
            }>
        ) {
            if (
                !state.data.evaluationSettings ||
                !Number.isInteger(payload.disputesAllowedPerEvaluation) ||
                payload.disputesAllowedPerEvaluation == null
            ) {
                return;
            }
            let disputesAllowed = payload.disputesAllowedPerEvaluation;

            if (disputesAllowed > DefaultLimits.maxDisputesPerEvaluation) {
                disputesAllowed = DefaultLimits.maxDisputesPerEvaluation;
            } else if (
                disputesAllowed < DefaultLimits.minDisputesPerEvaluation
            ) {
                disputesAllowed = DefaultLimits.minDisputesPerEvaluation;
            }

            state.data.evaluationSettings.disputesAllowedPerEvaluation =
                disputesAllowed;

            for (
                let i = state.data.evaluationSettings.disputeAssignments.length;
                i < disputesAllowed;
                i++
            ) {
                state.data.evaluationSettings.disputeAssignments.push({
                    assigneeUser: undefined,
                    type: EvaluationSettingsAssigneeTypeEnum.Original,
                });
            }
        },
        setAssigneeType(
            state,
            {
                payload,
            }: PayloadAction<{
                index: number;
                type: EvaluationSettingsAssigneeTypeEnum;
            }>
        ) {
            if (
                state.data.evaluationSettings &&
                payload.index <
                    state.data.evaluationSettings.disputeAssignments.length
            ) {
                state.data.evaluationSettings.disputeAssignments[
                    payload.index
                ].type = payload.type;
            }
        },
        setAssigneeUserError(
            state,
            {
                payload,
            }: PayloadAction<{
                index: number;
                error: boolean;
            }>
        ) {
            if (
                state.data.evaluationSettings &&
                payload.index <
                    state.data.evaluationSettings.disputeAssignments.length
            ) {
                state.data.evaluationSettings.disputeAssignments[
                    payload.index
                ].assigneeUserError = payload.error;
            }
        },
        setAssigneeUser(
            state,
            {
                payload,
            }: PayloadAction<{
                index: number;
                assigneeUser: SearchResult;
            }>
        ) {
            if (
                state.data.evaluationSettings &&
                payload.index <
                    state.data.evaluationSettings.disputeAssignments.length
            ) {
                state.data.evaluationSettings.disputeAssignments[
                    payload.index
                ].assigneeUser = payload.assigneeUser;
            }
        },
        toggleFormWeightMode(state) {
            const form = state.data.form;
            const formType = state.data.formType;
            if (form && formType) {
                const weightMode = form.weightMode;
                switch (weightMode) {
                    case EvaluationFormWeightModeEnum.SCALED:
                        form.weightMode = EvaluationFormWeightModeEnum.OFF;
                        break;
                    case EvaluationFormWeightModeEnum.OFF:
                        form.weightMode = EvaluationFormWeightModeEnum.SCALED;
                        break;
                }
            }
        },
        changeQuestionName(
            state,
            { payload }: PayloadAction<{ questionId: string; text: string }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.text = payload.text;
            }
        },
        changeQuestionHelpText(
            state,
            { payload }: PayloadAction<{ questionId: string; text: string }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.helpText = payload.text;
            }
        },
        changeAnswerText(
            state,
            {
                payload,
            }: PayloadAction<{
                answerOptionId: string;
                text: string;
                isMultipleSelect?: boolean;
            }>
        ) {
            const data = state.data;
            let answer;
            if (payload.isMultipleSelect) {
                answer =
                    data.multipleSelectOptionQuestions?.[
                        payload.answerOptionId
                    ];
            } else {
                answer = data.answerOptions?.[payload.answerOptionId];
            }
            if (answer) {
                answer.text = payload.text;
            }
        },
        changeAnswerValue(
            state,
            {
                payload,
            }: PayloadAction<{
                answerOptionId: string;
                value: string;
                isMultipleSelect?: boolean;
            }>
        ) {
            const answerStoringValue = selectAnswerOptionMutable(
                state,
                payload.answerOptionId,
                payload.isMultipleSelect
            );
            const points = Number(payload.value);
            if (answerStoringValue && isNonNegativeInteger(points)) {
                answerStoringValue.value = points;
            }
        },
        addAnswerOption(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                text?: string;
                value?: number;
                assistanceConditions?: Array<AssistanceCondition>;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (!state.data.answerOptions) {
                state.data.answerOptions = {};
            }
            const answerOptions = state.data.answerOptions;
            if (question) {
                const answerId = uuidv4();
                question.answerOptions.push(answerId);
                answerOptions[answerId] = {
                    id: answerId,
                    value: payload.value ?? 0,
                    text: payload.text,
                    assistanceConditions: payload.assistanceConditions,
                };
            }
        },
        addMultipleSelectOption(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                template: TemplateQuestion;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (!state.data.multipleSelectOptionQuestions) {
                state.data.multipleSelectOptionQuestions = {};
            }
            const pTempl = payload.template;
            let mSelectOptQuestionHash =
                state.data.multipleSelectOptionQuestions;
            if (question) {
                let mSelectOptQuestionIdList =
                    question.multipleSelectOptionQuestions;
                mSelectOptQuestionIdList = mSelectOptQuestionIdList || [];
                mSelectOptQuestionHash = mSelectOptQuestionHash || {};

                const mSelectOptQId = uuidv4();
                mSelectOptQuestionIdList.push(mSelectOptQId);
                mSelectOptQuestionHash[mSelectOptQId] = {
                    ...pTempl,
                    id: mSelectOptQId,
                    visibilityCondition: undefined,
                    answerOptions: pTempl.answerOptions?.map((option) => ({
                        ...option,
                        id: uuidv4(),
                    })),
                };

                const isMultipleSelect = true;
                const findSelectedOption = true;

                mSelectOptQuestionHash[mSelectOptQId].defaultAnswer =
                    pTempl.defaultAnswer
                        ? {
                              ...pTempl.defaultAnswer,
                              id: selectAnswerOptionMutable(
                                  state,
                                  mSelectOptQId,
                                  isMultipleSelect,
                                  findSelectedOption
                              )?.id,
                          }
                        : undefined;
            }
        },
        changeQuestionCommentsRequired(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.commentsRequired = !question.commentsRequired;
            }
        },
        setNAEnabledQuestion(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                value: boolean;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.naEnabled = payload.value;
            }
        },
        changeCriticalQuestion(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                isMultipleSelect?: boolean;
            }>
        ) {
            const question = selectScorableQuestion(
                { qualityForm: state } as RootState,
                payload.questionId,
                payload.isMultipleSelect
            );
            if (question) {
                question.isCritical = !question.isCritical;
            }
        },
        changeFatalQuestion(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                isMultipleSelect?: boolean;
            }>
        ) {
            const question = selectScorableQuestion(
                { qualityForm: state } as RootState,
                payload.questionId,
                payload.isMultipleSelect
            );
            // Fatal question must be critical
            if (question && question.isCritical) {
                question.isKill = !question.isKill;
            }
        },
        addQuestionToQuestionGroup(
            state,
            {
                payload,
            }: PayloadAction<{
                questionGroupId: string;
                template: TemplateQuestion;
                showEditMode?: boolean;
                isSurveyFreeTextLimitEnabled?: boolean;
            }>
        ) {
            const pTemplate = payload.template;
            const isFreeTextOrNps =
                pTemplate.type === BaseQuestionType.FreeTextQuestion ||
                pTemplate.type === BaseQuestionType.NpsQuestion;
            const formType = selectFormType({
                qualityForm: state,
            } as RootState);
            const isSurveyFreeText =
                pTemplate.type === BaseQuestionType.FreeTextQuestion &&
                formType === FormType.SURVEY;
            const initMaxResponseCharacters = isFreeTextOrNps
                ? isSurveyFreeText && payload.isSurveyFreeTextLimitEnabled
                    ? DefaultLimits.initMaxResponseCharactersSurveyFreeText
                    : DefaultLimits.initMaxResponseCharacters
                : undefined;
            const questionGroup =
                state.data.questionGroups?.[payload.questionGroupId];
            const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm({
                qualityForm: state,
            } as RootState);
            if (questionGroup) {
                if (!questionGroup.questions) {
                    questionGroup.questions = [];
                }
                if (!state.data.questions) {
                    state.data.questions = {};
                }
                const questionList = questionGroup.questions;
                const newQuestionId: string = uuidv4();
                questionList.push(newQuestionId);

                // Check if any answer options in template have assistance conditions
                const hasAssistanceConditions = pTemplate.answerOptions?.some(
                    (answerOption) =>
                        answerOption.assistanceConditions &&
                        answerOption.assistanceConditions.length > 0
                );

                const isMultipleSelect =
                    pTemplate.type ===
                    EvaluationQuestionTypeEnum.MultipleSelectQuestion;
                // only multiple select questions should have an undefined automatedScoringType
                let automatedScoringType = AutomatedScoringType.NO_AUTOMATION;
                if (hasAssistanceConditions) {
                    automatedScoringType =
                        AutomatedScoringType.EVALUATION_ASSISTANCE;
                }

                // Check if question group has user defined default answers enabled
                const defaultAnswersTo =
                    getInitializedDefaultAnswersTo(questionGroup);
                const isUserDefinedEnabled = defaultAnswersTo.userDefined;

                // Create question object, conditionally including defaultAnswer if user defined is enabled
                state.data.questions[newQuestionId] = {
                    id: newQuestionId,
                    ...pTemplate,
                    defaultAnswer: undefined,
                    answerOptions: [],
                    multipleSelectOptionQuestions: [],
                    maxResponseCharacters: initMaxResponseCharacters,
                    explanationPrompt: undefined,
                    showEditMode: payload.showEditMode ?? false,
                    agreementRate: undefined, // Initialize agreement rate property
                    ...(isAutomatedEvaluationForm
                        ? {
                              naEnabled: questionGroup.defaultAnswersTo
                                  ?.notApplicable
                                  ? pTemplate.naEnabled
                                  : false,
                          }
                        : {}),
                    ...(!isMultipleSelect ? { automatedScoringType } : {}),
                    ...(!isMultipleSelect &&
                    automatedScoringType !== AutomatedScoringType.NO_AUTOMATION
                        ? {
                              // Newly added questions default to EvaluatedAgent
                              automatedScoringFocus:
                                  EvaluationQuestionAutomatedScoringFocusEnum.EvaluatedAgent,
                          }
                        : {}),
                };
                if (pTemplate.answerOptions) {
                    pTemplate.answerOptions.forEach((answerOption) => {
                        const templateAnswerId = answerOption.id;

                        slice.caseReducers.addAnswerOption(state, {
                            payload: {
                                questionId: newQuestionId,
                                text: answerOption.text,
                                value: answerOption.value,
                                assistanceConditions:
                                    answerOption.assistanceConditions,
                            },
                            type: "",
                        });

                        // Get the newly created answer option ID (last one added)
                        const question = state.data.questions?.[newQuestionId];
                        if (question) {
                            const newAnswerId =
                                question.answerOptions[
                                    question.answerOptions.length - 1
                                ];

                            if (
                                templateAnswerId != null &&
                                templateAnswerId ===
                                    pTemplate.defaultAnswer?.id &&
                                isUserDefinedEnabled
                            ) {
                                question.defaultAnswer = {
                                    ...pTemplate.defaultAnswer,
                                    id: newAnswerId,
                                };
                            }
                        }
                    });
                }

                if (pTemplate.multipleSelectOptionQuestions) {
                    pTemplate.multipleSelectOptionQuestions.forEach(
                        (multipleSelectOptionQuestion) => {
                            // Destructure template to separate defaultAnswer from other properties
                            const {
                                defaultAnswer: templateDefaultAnswer,
                                ...restOfTemplate
                            } = multipleSelectOptionQuestion;

                            slice.caseReducers.addMultipleSelectOption(state, {
                                payload: {
                                    questionId: newQuestionId,
                                    template: {
                                        answerOptions: undefined,
                                        automatedScoringFocus: undefined,
                                        commentsRequired: false,
                                        contextId: undefined,
                                        explanationPrompt: undefined,
                                        helpText: "",
                                        isCritical: false,
                                        isKill: false,
                                        maxResponseCharacters: 0,
                                        multipleSelectOptionQuestions: [],
                                        naEnabled: false,
                                        text: "",
                                        type: pTemplate.type,
                                        ...restOfTemplate,
                                        // Only include defaultAnswer if user defined is enabled
                                        defaultAnswer: isUserDefinedEnabled
                                            ? templateDefaultAnswer
                                            : undefined,
                                    },
                                },
                                type: "",
                            });
                        }
                    );
                }
            }
        },
        addNewQuestionGroup(state) {
            const form = state.data;
            if (!form.questionGroups) {
                form.questionGroups = {};
            }
            const questionGroupsList = form.form?.questionGroups;
            const questionGroupId = uuidv4();

            if (questionGroupsList) {
                questionGroupsList.push(questionGroupId);
                form.questionGroups[questionGroupId] = {
                    id: questionGroupId,
                    type: "questionGroup",
                    name: "",
                    defaultAnswersToHighest: false,
                    defaultAnswersToNA: false,
                    defaultAnswersTo: {
                        highestScore: false,
                        notApplicable: false,
                        lowestScore: false,
                        userDefined: false,
                    },
                    naEnabled: false,
                    weight: 100,
                    manualWeight: false,
                    questions: [],
                    selfUri: "",
                    showEditMode: true,
                    contextId: undefined,
                };
            }
        },
        changeFormName(state, { payload }: { payload: string }) {
            const form = state.data.form;
            if (form) {
                form.name = payload;
            }
        },
        changePassPercent(state, { payload }: { payload: number }) {
            const form = state.data.form;
            if (form) {
                form.passPercent = payload;
            }
        },
        deleteAnswerOption(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                answerOptionId: string;
                isMultipleSelect: boolean;
            }>
        ) {
            const question = selectQuestion(
                {
                    qualityForm: state,
                } as RootState,
                payload.questionId
            );
            if (!question) {
                return;
            }
            if (payload.isMultipleSelect) {
                if (
                    state.data.multipleSelectOptionQuestions &&
                    question.multipleSelectOptionQuestions &&
                    question.multipleSelectOptionQuestions.length > 1
                ) {
                    question.multipleSelectOptionQuestions =
                        question.multipleSelectOptionQuestions.filter(
                            (answerOption) =>
                                answerOption !== payload.answerOptionId
                        );
                    delete state.data.multipleSelectOptionQuestions[
                        payload.answerOptionId
                    ];
                }
            } else {
                if (
                    state.data.answerOptions &&
                    question.answerOptions &&
                    question.answerOptions.length > 2
                ) {
                    question.answerOptions = question.answerOptions.filter(
                        (answerOption) =>
                            answerOption !== payload.answerOptionId
                    );
                    delete state.data.answerOptions[payload.answerOptionId];
                }
            }
        },
        deleteQuestion(
            state,
            {
                payload,
            }: {
                payload: string;
            }
        ) {
            const questionGroups = state.data.questionGroups;
            if (questionGroups) {
                const questionGroup = Object.values(questionGroups).find(
                    (questionGroup) => questionGroup.questions.includes(payload)
                );
                if (state.data.questions && questionGroup?.questions) {
                    questionGroup.questions = questionGroup.questions.filter(
                        (questionId) => questionId !== payload
                    );
                    const answerOptionIds =
                        state.data.questions[payload].answerOptions;
                    delete state.data.questions[payload];
                    answerOptionIds?.forEach((answerOptionId) => {
                        delete state.data.answerOptions?.[answerOptionId];
                    });
                    // update in the aiScoring field (only for Evaluation)
                    if (state.data.formType === FormType.EVALUATION) {
                        slice.caseReducers.removeAIScoringSettingForQuestion(
                            state,
                            {
                                payload: {
                                    questionGroupId: questionGroup.id,
                                    questionId: payload,
                                },
                                type: "",
                            }
                        );
                    }
                }
            }
        },
        deleteQuestionGroup(
            state,
            {
                payload,
            }: {
                payload: string;
            }
        ) {
            const form = state.data.form;

            if (
                form?.questionGroups &&
                state.data.questionGroups &&
                form.questionGroups.length > 1
            ) {
                form.questionGroups = form.questionGroups.filter(
                    (questionGroupId) => questionGroupId !== payload
                );
                const questionIds =
                    state.data.questionGroups?.[payload].questions;

                delete state.data.questionGroups[payload];

                questionIds?.forEach((questionId) => {
                    const answerOptionIds =
                        state.data.questions?.[questionId].answerOptions;
                    delete state.data.questions?.[questionId];
                    answerOptionIds?.forEach((answerOptionId) => {
                        delete state.data.answerOptions?.[answerOptionId];
                    });
                });

                //update in the aiScoring field (only for Evaluation)
                if (state.data.formType === FormType.EVALUATION) {
                    // delete AI Scoring settings for all the questions for this question group
                    const questionGroupSetting =
                        state.data.aiScoring?.questionGroupSettings?.[payload];
                    if (questionGroupSetting) {
                        delete state.data.aiScoring?.questionGroupSettings?.[
                            payload
                        ];
                    }
                }
            }
        },
        updateVisibilityPredicateQuestion(
            state,
            {
                payload,
            }: PayloadAction<{
                id: string;
                conditionalQuestionId: string;
                predicateIndex: number;
                formEntity: "question" | "questionGroup";
                isMultipleSelectFTEnabled: boolean;
            }>
        ) {
            const formEntityData =
                payload.formEntity === "question"
                    ? state.data.questions
                    : state.data.questionGroups;
            const predicate =
                formEntityData?.[payload.id].visibilityCondition?.predicates?.[
                    payload.predicateIndex
                ];
            const questionGroupId =
                Object.values(state.data?.questionGroups ?? []).find((qg) =>
                    qg.questions.includes(payload.conditionalQuestionId)
                )?.id ?? "";
            const eligibleAnswers =
                payload.formEntity === "question"
                    ? selectEligibleAnswersForVisibilityConditionInQuestion(
                          state,
                          payload.conditionalQuestionId,
                          payload.id,
                          payload.isMultipleSelectFTEnabled
                      )
                    : selectEligibleAnswersForVisibilityConditionInQuestionGroup(
                          state,
                          payload.conditionalQuestionId,
                          payload.id,
                          payload.isMultipleSelectFTEnabled
                      );
            if (predicate) {
                predicate.questionId = payload.conditionalQuestionId;
                predicate.questionGroupId = questionGroupId;
                predicate.answerId =
                    (eligibleAnswers.length ?? 0) > 0 ? eligibleAnswers[0] : "";
            }
        },
        updateVisibilityPredicateAnswer(
            state,
            {
                payload,
            }: PayloadAction<{
                conditionalAnswerId: string;
                predicateIndex: number;
                id: string;
                formEntity: "question" | "questionGroup";
            }>
        ) {
            const formEntityData =
                payload.formEntity === "question"
                    ? state.data.questions
                    : state.data.questionGroups;

            const predicate =
                formEntityData?.[payload.id].visibilityCondition?.predicates[
                    payload.predicateIndex
                ];
            if (predicate) {
                predicate.answerId = payload.conditionalAnswerId;
            }
        },
        addVisibilityPredicate(
            state,
            {
                payload,
            }: PayloadAction<{
                id: string;
                formEntity: "question" | "questionGroup";
                defaultQuestionId: string;
                isMultipleSelectFTEnabled: boolean;
            }>
        ) {
            if (payload.defaultQuestionId && payload.defaultQuestionId != "") {
                const formEntityData =
                    payload.formEntity === "question"
                        ? state.data.questions
                        : state.data.questionGroups;

                const defaultAnswerId =
                    payload.formEntity === "question"
                        ? selectEligibleAnswersForVisibilityConditionInQuestion(
                              state,
                              payload.defaultQuestionId,
                              payload.id,
                              payload.isMultipleSelectFTEnabled
                          )[0]
                        : selectEligibleAnswersForVisibilityConditionInQuestionGroup(
                              state,
                              payload.defaultQuestionId,
                              payload.id,
                              payload.isMultipleSelectFTEnabled
                          )[0];
                formEntityData?.[
                    payload.id
                ].visibilityCondition?.predicates.push({
                    questionGroupId:
                        selectQuestionGroupIdContainingQuestion(
                            payload.defaultQuestionId
                        )(state) ?? "",
                    questionId: payload.defaultQuestionId,
                    answerId: defaultAnswerId,
                });
            }
        },
        deleteVisibilityPredicate(
            state,
            {
                payload,
            }: PayloadAction<{
                id: string;
                formEntity: "question" | "questionGroup";
                predicateIndex: number;
            }>
        ) {
            const formEntity =
                payload.formEntity === "question"
                    ? state.data.questions?.[payload.id]
                    : state.data.questionGroups?.[payload.id];

            if (formEntity && formEntity.visibilityCondition?.predicates) {
                formEntity.visibilityCondition.predicates =
                    formEntity.visibilityCondition?.predicates.filter(
                        (pred, index) => index !== payload.predicateIndex
                    );
            }
        },
        toggleConditionalDisplay(
            state,
            {
                payload,
            }: PayloadAction<{
                id: string;
                formEntity: "question" | "questionGroup";
                defaultQuestionGroupId?: string;
                defaultQuestionId?: string;
                defaultAnswerId?: string;
            }>
        ) {
            const formEntity =
                payload.formEntity === "question"
                    ? state.data.questions?.[payload.id]
                    : state.data.questionGroups?.[payload.id];

            if (formEntity) {
                if (
                    !formEntity?.visibilityCondition &&
                    payload.defaultQuestionGroupId &&
                    payload.defaultQuestionId &&
                    payload.defaultAnswerId
                ) {
                    formEntity.visibilityCondition = {
                        combiningOperation:
                            VisibilityConditionCombiningOperationEnum.OR,
                        predicates: [
                            {
                                questionGroupId: payload.defaultQuestionGroupId,
                                questionId: payload.defaultQuestionId,
                                answerId: payload.defaultAnswerId,
                            },
                        ],
                    };
                } else {
                    formEntity.visibilityCondition = undefined;
                }
            }
        },
        changeVisibilityConditionCombiningOperator(
            state,
            {
                payload,
            }: PayloadAction<{
                id: string;
                formEntity: "question" | "questionGroup";
                conditionalOperator: VisibilityConditionCombiningOperationEnum;
            }>
        ) {
            const formEntity =
                payload.formEntity === "question"
                    ? state.data.questions?.[payload.id]
                    : state.data.questionGroups?.[payload.id];
            if (formEntity?.visibilityCondition) {
                formEntity.visibilityCondition.combiningOperation =
                    payload.conditionalOperator;
            }
        },
        moveQuestionGroup(
            state,
            {
                payload,
            }: PayloadAction<{
                questionGroupId: string;
                newRelativePosition: number;
                indexOfSelectedQuestionGroup: number;
            }>
        ) {
            const questionGroups = state.data.form?.questionGroups;
            if (questionGroups) {
                questionGroups.splice(
                    payload.indexOfSelectedQuestionGroup +
                        payload.newRelativePosition,
                    0,
                    questionGroups?.splice(
                        payload.indexOfSelectedQuestionGroup,
                        1
                    )[0]
                );
            }
        },
        moveQuestion(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                newRelativePosition: number;
                questionGroupId: string;
                indexOfSelectedQuestionWithinQuestionGroup: number;
            }>
        ) {
            const questions =
                state.data?.questionGroups?.[payload.questionGroupId].questions;

            if (questions) {
                questions.splice(
                    payload.indexOfSelectedQuestionWithinQuestionGroup +
                        payload.newRelativePosition,
                    0,
                    questions?.splice(
                        payload.indexOfSelectedQuestionWithinQuestionGroup,
                        1
                    )[0]
                );
            }
        },
        moveAnswer(
            state,
            {
                payload,
            }: PayloadAction<{
                answerOptionId: string;
                newRelativePosition: number;
                questionId: string;
                indexOfSelectedAnswerWithinQuestion: number;
            }>
        ) {
            const question = state.data?.questions?.[payload.questionId];
            if (!question) {
                return;
            }
            let answers;
            if (
                question.type ===
                EvaluationQuestionTypeEnum.MultipleSelectQuestion
            ) {
                answers = question.multipleSelectOptionQuestions;
            } else {
                answers = question.answerOptions;
            }

            if (answers) {
                answers.splice(
                    payload.indexOfSelectedAnswerWithinQuestion +
                        payload.newRelativePosition,
                    0,
                    answers?.splice(
                        payload.indexOfSelectedAnswerWithinQuestion,
                        1
                    )[0]
                );
            }
        },
        updateHeader(state, { payload }: { payload: string }) {
            const form = state.data.form;
            if (form) {
                form.header = payload;
            }
        },
        clearHeader(state) {
            const form = state.data.form;
            if (form) {
                form.header = undefined;
            }
        },
        updateFooter(state, { payload }: { payload: string }) {
            const form = state.data.form;
            if (form) {
                form.footer = payload;
            }
        },
        clearFooter(state) {
            const form = state.data.form;
            if (form) {
                form.footer = undefined;
            }
        },
        deleteEvaluationAssistanceCondition(
            state,
            {
                payload,
            }: PayloadAction<{
                answerId: string;
                index: number;
                isMultipleSelect: boolean;
            }>
        ) {
            const answerOption = selectAnswerOptionMutable(
                state,
                payload.answerId,
                payload.isMultipleSelect
            );
            if (!answerOption) return;
            if (answerOption.assistanceConditions) {
                if (answerOption.assistanceConditions.length === 1) {
                    answerOption.assistanceConditions = undefined;
                } else {
                    answerOption.assistanceConditions =
                        answerOption.assistanceConditions.filter(
                            (condition, index) => payload.index !== index
                        );
                }
            }
        },
        addEvaluationAssistanceCondition(
            state,
            {
                payload,
            }: PayloadAction<{
                answerId: string;
                isMultipleSelect: boolean;
            }>
        ) {
            const answer = selectAnswerOptionMutable(
                state,
                payload.answerId,
                payload.isMultipleSelect
            );
            if (answer) {
                const newCondition = {
                    operator: AssistanceConditionOperatorEnum.EXISTS,
                    topicIds: [],
                };
                if (answer?.assistanceConditions === undefined) {
                    answer["assistanceConditions"] = [newCondition];
                } else {
                    answer.assistanceConditions.push(newCondition);
                }
            }
        },
        updateAssistanceConditionOperator(
            state,
            {
                payload,
            }: PayloadAction<{
                answerId: string;
                index: number;
                operator: AssistanceConditionOperatorEnum;
                isMultipleSelect: boolean;
            }>
        ) {
            const answer = selectAnswerOptionMutable(
                state,
                payload.answerId,
                payload.isMultipleSelect
            );
            if (answer?.assistanceConditions) {
                answer.assistanceConditions[payload.index].operator =
                    payload.operator;
            }
        },
        updateAssistanceConditionTopics(
            state,
            {
                payload,
            }: PayloadAction<{
                answerId: string;
                isMultipleSelect: boolean;
                index: number;
                selectedTopics?: Array<string>;
            }>
        ) {
            const answer = selectAnswerOptionMutable(
                state,
                payload.answerId,
                payload.isMultipleSelect
            );
            if (answer?.assistanceConditions) {
                answer.assistanceConditions[payload.index].topicIds =
                    payload.selectedTopics;
            }
        },
        setMaxResponseCharacters(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                maxResponseCharacters: string;
                effectiveMax?: number;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            const maxCharCount = Number(payload.maxResponseCharacters);
            const effectiveMax =
                payload.effectiveMax ?? DefaultLimits.maxResponseCharacters;
            if (question) {
                if (maxCharCount < DefaultLimits.minResponseCharacters) {
                    question.maxResponseCharacters =
                        DefaultLimits.minResponseCharacters;
                } else if (maxCharCount > effectiveMax) {
                    question.maxResponseCharacters = effectiveMax;
                } else {
                    question.maxResponseCharacters = maxCharCount;
                }
            }
        },
        setExplanationPrompt(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                explanationPrompt: string | undefined;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.explanationPrompt = payload.explanationPrompt;
            }
        },
        setFormLanguage(state, { payload }: PayloadAction<Locale>) {
            const form = state.data.form;
            if (form) {
                form.language = payload;
            }
        },

        removeExplanationPrompt(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
            }>
        ) {
            slice.caseReducers.setExplanationPrompt(state, {
                payload: {
                    questionId: payload.questionId,
                    explanationPrompt: undefined,
                },
                type: "",
            });
            slice.caseReducers.setNAEnabledQuestion(state, {
                payload: {
                    questionId: payload.questionId,
                    value: false,
                },
                type: "",
            });
            slice.caseReducers.setMaxResponseCharacters(state, {
                payload: {
                    questionId: payload.questionId,
                    maxResponseCharacters: String(
                        DefaultLimits.initMaxResponseCharacters
                    ),
                    effectiveMax: DefaultLimits.maxResponseCharacters,
                },
                type: "",
            });
        },
        switchEditModeForQuestion(
            state,
            {
                payload,
            }: PayloadAction<{ questionId: string; showEditMode: boolean }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.showEditMode = payload.showEditMode;
            }
        },
        switchEditModeForQuestionGroup(
            state,
            {
                payload,
            }: PayloadAction<{ questionGroupId: string; showEditMode: boolean }>
        ) {
            const questionGroup =
                state.data.questionGroups?.[payload.questionGroupId];
            if (questionGroup) {
                questionGroup.showEditMode = payload.showEditMode;
            }
        },
        removeAIScoringSettingForQuestion(
            state,
            {
                payload,
            }: PayloadAction<{
                questionGroupId: string | undefined;
                questionId: string | undefined;
            }>
        ) {
            if (
                state.data.formType === FormType.EVALUATION &&
                state.data.aiScoring &&
                payload.questionGroupId
            ) {
                const questionGroupSetting =
                    state.data.aiScoring.questionGroupSettings?.[
                        payload.questionGroupId
                    ];

                if (questionGroupSetting) {
                    if (
                        payload.questionId &&
                        payload.questionId in
                            questionGroupSetting.questionSettings
                    ) {
                        delete questionGroupSetting.questionSettings[
                            payload.questionId
                        ];

                        if (
                            Object.keys(questionGroupSetting.questionSettings)
                                .length === 0
                        ) {
                            delete state.data.aiScoring.questionGroupSettings[
                                payload.questionGroupId
                            ];
                        }
                    }
                }
            }
        },
        removeEvaluationAssistanceForQuestion(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            const answerOptionIds = question?.answerOptions;
            if (answerOptionIds) {
                for (const optionId of answerOptionIds) {
                    const answerOption = state.data.answerOptions?.[optionId];
                    if (answerOption?.assistanceConditions !== undefined) {
                        answerOption.assistanceConditions = undefined;
                    }
                }
            }
        },
        setAutomatedScoringOption(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                automatedScoringOption: AutomatedScoringType;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.automatedScoringType = payload.automatedScoringOption;
                const questionGroupId = Object.values(
                    state.data.questionGroups ?? {}
                ).find((questionGroup) =>
                    questionGroup.questions.includes(payload.questionId)
                )?.id;

                switch (payload.automatedScoringOption) {
                    case AutomatedScoringType.NO_AUTOMATION:
                        // remove AI scoring configuration
                        slice.caseReducers.removeAIScoringSettingForQuestion(
                            state,
                            {
                                payload: {
                                    questionGroupId: questionGroupId,
                                    questionId: payload.questionId,
                                },
                                type: "",
                            }
                        );

                        // remove any eval assistance for all answer options for this question
                        slice.caseReducers.removeEvaluationAssistanceForQuestion(
                            state,
                            {
                                payload: {
                                    questionId: payload.questionId,
                                },
                                type: "",
                            }
                        );

                        // clear evaluation focus when automation is removed
                        question.automatedScoringFocus = undefined;

                        break;
                    case AutomatedScoringType.AI_SCORING:
                        // set AI scoring configuration
                        if (
                            state.data.formType === FormType.EVALUATION &&
                            state.data.aiScoring &&
                            questionGroupId
                        ) {
                            if (
                                !(
                                    questionGroupId in
                                    state.data.aiScoring.questionGroupSettings
                                )
                            ) {
                                state.data.aiScoring.questionGroupSettings[
                                    questionGroupId
                                ] = {
                                    questionSettings: {},
                                };
                            }
                            state.data.aiScoring.questionGroupSettings[
                                questionGroupId
                            ].questionSettings[payload.questionId] = {
                                enabled: true,
                            };
                        }

                        // remove any eval assistance for all answer options for this question
                        slice.caseReducers.removeEvaluationAssistanceForQuestion(
                            state,
                            {
                                payload: {
                                    questionId: payload.questionId,
                                },
                                type: "",
                            }
                        );

                        question.automatedScoringFocus = question.contextId
                            ? EvaluationQuestionAutomatedScoringFocusEnum.FullInteraction
                            : EvaluationQuestionAutomatedScoringFocusEnum.EvaluatedAgent;

                        break;
                    case AutomatedScoringType.EVALUATION_ASSISTANCE:
                        // remove AI scoring configuration
                        slice.caseReducers.removeAIScoringSettingForQuestion(
                            state,
                            {
                                payload: {
                                    questionGroupId: questionGroupId,
                                    questionId: payload.questionId,
                                },
                                type: "",
                            }
                        );

                        question.automatedScoringFocus = question.contextId
                            ? EvaluationQuestionAutomatedScoringFocusEnum.FullInteraction
                            : EvaluationQuestionAutomatedScoringFocusEnum.EvaluatedAgent;

                        break;
                }
            }
        },
        setAutomatedScoringFocus(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                automatedScoringFocus: EvaluationQuestionAutomatedScoringFocusEnum;
            }>
        ) {
            const question = state.data.questions?.[payload.questionId];
            if (question) {
                question.automatedScoringFocus = payload.automatedScoringFocus;
            }
        },
        setAiScoringSettings(state) {
            const previousSettings = state.data.form?.aiScoring;
            const questionGroups = state.data.questionGroups;
            const questions = state.data.questions;
            const formattedAiScoringSettings = fromFormAiScoringSettings(
                previousSettings,
                questionGroups,
                questions
            );
            state.data.aiScoring = formattedAiScoringSettings;
        },
        setIsFormModified(
            state,
            {
                payload,
            }: PayloadAction<{
                isModified: boolean;
            }>
        ) {
            state.data.isModified = payload?.isModified;
        },
        setDivision: (
            state,
            {
                payload,
            }: { payload: { id: string; name: string; selfUri: string } | null }
        ) => {
            if (state.data.form) {
                (state.data.form as any).division = payload;
            }
        },
        setScreenReaderAnnouncement(state, action: PayloadAction<string>) {
            state.data.screenReaderAnnouncement = action.payload;
        },
        clearScreenReaderAnnouncement(state) {
            state.data.screenReaderAnnouncement = "";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(loadForm.rejected, (state, action) => {
                const payload = action.payload as any;

                state.data = {
                    ...state.data,
                    answerOptions: undefined,
                    questionGroups: undefined,
                    questions: undefined,
                    form: undefined,
                    formType: undefined,
                    loadError: payload?.status ? payload : undefined,
                };

                // Any API error (4xx/5xx) - show toast and redirect to forms list
                if (payload?.titleKey && payload?.messageKey) {
                    toastService.notifyMajorAlert(payload.titleKey, {
                        bodyMessageKey: payload.messageKey,
                    });
                }

                // Redirect to forms list is done in route components so they can
                // use the admin UI decomposition feature toggle for the correct path.
            })
            .addCase(loadFormNames.fulfilled, (state, { payload }) => {
                state.data.formNames = payload;
            })
            .addCase(loadFormNames.rejected, (state) => {
                state.data.formNames = undefined;
            })
            .addCase(saveForm.rejected, (response) => {
                console.log(
                    "error: save form operation did not succeed",
                    response
                );
            })
            .addCase(publishForm.rejected, () => {
                console.log("error: publish form operation did not succeed");
            })
            .addCase(disableSurveyForm.rejected, () => {
                console.log(
                    "error: survey disabled form operation did not succeed"
                );
            })
            .addCase(saveAiScoringSettings.rejected, () => {
                console.log("error: save ai scoring settings did not succeed");
            })
            .addCase(saveAiScoringSettings.fulfilled, (state, action) => {
                const payload = action.payload;
                const questionGroups = state.data.questionGroups;
                const questions = state.data.questions;
                const formattedAiScoringSettings = fromFormAiScoringSettings(
                    payload,
                    questionGroups,
                    questions
                );
                state.data.aiScoring = formattedAiScoringSettings;
            })
            .addCase(loadAgreementRate.pending, (state) => {
                // Set loading state for form
                if (state.data.form) {
                    if (!state.data.form.agreementRate) {
                        state.data.form.agreementRate = { loading: false };
                    }
                    state.data.form.agreementRate.loading = true;
                    state.data.form.agreementRate.error = undefined;
                    state.data.form.agreementRate.formAgreementRate = undefined;
                    state.data.form.agreementRate.latestPublishedFormId =
                        undefined;
                }
            })
            .addCase(loadAgreementRate.fulfilled, (state, action) => {
                // Update form agreement rate data
                if (state.data.form) {
                    if (!state.data.form.agreementRate) {
                        state.data.form.agreementRate = { loading: false };
                    }
                    state.data.form.agreementRate.loading = false;
                    state.data.form.agreementRate.formAgreementRate =
                        action.payload.form;
                    state.data.form.agreementRate.error = undefined;
                    state.data.form.agreementRate.latestPublishedFormId =
                        action.payload.latestPublishedFormId;
                }

                // Update question agreement rate data
                if (state.data.questions && action.payload.questions) {
                    Object.entries(action.payload.questions).forEach(
                        ([questionId, agreementRateData]) => {
                            const question = state.data.questions?.[questionId];
                            if (question) {
                                question.agreementRate = agreementRateData;
                            }
                        }
                    );
                }
            })
            .addCase(loadAgreementRate.rejected, (state, action) => {
                // Clear form agreement rate data on error
                if (state.data.form) {
                    if (!state.data.form.agreementRate) {
                        state.data.form.agreementRate = { loading: false };
                    }
                    state.data.form.agreementRate.loading = false;
                    state.data.form.agreementRate.formAgreementRate = undefined;
                    state.data.form.agreementRate.error =
                        action.payload as string;
                    state.data.form.agreementRate.latestPublishedFormId =
                        undefined;
                }

                // Clear question agreement rate data on error
                if (state.data.questions) {
                    Object.keys(state.data.questions).forEach((questionId) => {
                        const question = state.data.questions?.[questionId];
                        if (question && question.agreementRate) {
                            question.agreementRate = undefined;
                        }
                    });
                }
                console.error(
                    "Failed to load agreement rates:",
                    action.payload
                );
            })
            .addMatcher(
                isAnyOf(
                    saveForm.fulfilled,
                    loadForm.fulfilled,
                    publishForm.fulfilled,
                    disableSurveyForm.fulfilled
                ),
                (state, action) => {
                    const payload = action.payload;
                    if (payload === undefined || "settingsError" in payload) {
                        return;
                    }
                    const formType =
                        action.meta.arg?.formType ?? state.data.formType;

                    // take out the form response from the payload if the action was from saveForm with saving ai scoring configurations
                    const payloadForm =
                        "evaluationForm" in payload
                            ? payload.evaluationForm
                            : payload;

                    addAnswerInfoToVisibilityPredicates(payloadForm);

                    // Preserve division before normalization (it's not in the schema)
                    // Both EvaluationFormResponse and SurveyForm have division property
                    const formDivision = (
                        payloadForm as EvaluationFormResponse | SurveyForm
                    ).division;

                    const { entities, result } = normalize(
                        payloadForm,
                        normalizedQualityForm
                    );

                    if (entities.form && result) {
                        state.data = {
                            ...state.data,
                            ...entities,
                            form: {
                                ...entities.form[result],
                                agreementRate: state.data.form
                                    ?.agreementRate || {
                                    loading: false,
                                },
                                ...(formDivision && { division: formDivision }),
                            },
                            formType: formType,
                            isModified: false,
                        };
                        // Initialize defaultAnswersTo for existing question groups
                        if (
                            state.data.questionGroups &&
                            formType === FormType.EVALUATION
                        ) {
                            Object.values(state.data.questionGroups).forEach(
                                (questionGroup) => {
                                    getInitializedDefaultAnswersTo(
                                        questionGroup
                                    );
                                }
                            );
                        }
                    }
                    if (
                        "evaluationSettings" in payloadForm &&
                        payloadForm.evaluationSettings
                    ) {
                        state.data.evaluationSettings =
                            fromFormEvaluationSettings(
                                payloadForm.evaluationSettings
                            );
                    } else {
                        const settings = getEvaluationSettingsFromStorage(
                            state.data.form?.id
                        );
                        if (settings) {
                            state.data.evaluationSettings = settings;
                        }
                    }

                    // Set AI Scoring settings
                    // Skip if
                    // 1) there's aiScoringSettingsError in the payload (want to have current settings staying for retry)
                    // 2) it is not for evaluation form
                    const isEvaluationFormResponse = (
                        payload: any
                    ): payload is EvaluationFormResponse => {
                        return (
                            formType === FormType.EVALUATION &&
                            !("aiScoringSettingsError" in payload)
                        );
                    };

                    if (isEvaluationFormResponse(payload)) {
                        const questionGroups = state.data.questionGroups;
                        const questions = state.data.questions;

                        const formattedAiScoringSettings =
                            fromFormAiScoringSettings(
                                payload.aiScoring,
                                questionGroups,
                                questions
                            );
                        state.data.aiScoring = formattedAiScoringSettings;
                    }

                    // Set automateScoringType for EA which is needed to
                    // load add assistance button properly on edit mode toggle
                    const questions = state.data.questions || {};
                    const answerOptions = state.data.answerOptions || {};
                    Object.keys(questions).forEach((key) => {
                        const question = questions[key];

                        const isMultipleSelect =
                            question.type ===
                            EvaluationQuestionTypeEnum.MultipleSelectQuestion;

                        const hasAssistanceConditions =
                            question.answerOptions?.some((id) => {
                                const answerOption = answerOptions[id];
                                return !!answerOption.assistanceConditions
                                    ?.length;
                            });

                        const hasAiScoring = Object.values(
                            state.data.aiScoring?.questionGroupSettings ?? {}
                        ).some((questionGroupSetting) => {
                            const questionSettings =
                                questionGroupSetting.questionSettings;
                            return (
                                key in questionSettings &&
                                questionSettings[key].enabled
                            );
                        });

                        if (!isMultipleSelect) {
                            // only multiple select questions should have an undefined automatedScoringType
                            if (hasAssistanceConditions) {
                                question.automatedScoringType =
                                    AutomatedScoringType.EVALUATION_ASSISTANCE;
                            } else if (hasAiScoring) {
                                question.automatedScoringType =
                                    AutomatedScoringType.AI_SCORING;
                            } else {
                                question.automatedScoringType =
                                    AutomatedScoringType.NO_AUTOMATION;
                            }
                        }
                    });
                }
            )
            .addMatcher(
                (action) =>
                    action.type.match("^qualityForm/.*") &&
                    !action.type.match(".*/pending|fulfilled|rejected$") &&
                    action.type !== "qualityForm/setIsFormModified" &&
                    action.type !== "qualityForm/setDivision",
                (state) => {
                    state.data.isModified = true;
                }
            );
    },
});

function getEvaluationSettingsFromStorage(
    id?: string
): evaluationSettingsState | undefined {
    const settingsString = sessionStorage.getItem("evaluationSettings");
    if (settingsString) {
        try {
            const settings = JSON.parse(settingsString);
            if (id && id in settings) {
                sessionStorage.removeItem("evaluationSettings");
                return settings[id];
            }
        } catch {
            sessionStorage.removeItem("evaluationSettings");
            return undefined;
        }
    }
    return undefined;
}

function toFormEvaluationSettings(
    state: evaluationSettingsState
): EvaluationSettings {
    return {
        revisionsEnabled: state.revisionsEnabled,
        disputesEnabled: state.disputesEnabled,
        disputesAllowedPerEvaluation: state.disputesAllowedPerEvaluation,
        disputesAssignees: state.disputeAssignments
            .slice(0, state.disputesAllowedPerEvaluation)
            .map((assignee) => {
                return {
                    type: assignee.type,
                    user:
                        assignee.type !==
                        EvaluationSettingsAssigneeTypeEnum.Individual
                            ? undefined
                            : {
                                  id: assignee.assigneeUser?.value,
                                  name: assignee.assigneeUser?.name,
                              },
                };
            }),
    };
}

function fromFormEvaluationSettings(
    settings: EvaluationSettings
): evaluationSettingsState {
    return {
        revisionsEnabled: settings.revisionsEnabled ?? false,
        disputesEnabled: settings.disputesEnabled ?? false,
        disputesAllowedPerEvaluation:
            settings.disputesAllowedPerEvaluation ??
            DefaultLimits.defaultDisputesPerEvaluation,
        disputeAssignments:
            settings.disputesAssignees?.map((assignee) => {
                return {
                    assigneeUser: assignee.user
                        ? {
                              name: assignee.user.name ?? "",
                              value: assignee.user.id ?? "",
                              version: 0,
                          }
                        : undefined,
                    type:
                        assignee.type ??
                        EvaluationSettingsAssigneeTypeEnum.Original,
                };
            }) ??
            Array.from(
                {
                    length: DefaultLimits.defaultDisputesPerEvaluation,
                },
                () => ({
                    type: EvaluationSettingsAssigneeTypeEnum.Original,
                })
            ),
    };
}

//TODO : make use of qualityFormActions everywhere.
export const { setQuestionGroupWeight, toggleFormWeightMode } = slice.actions;

export const qualityFormActions = slice.actions;

export const selectIsModified = (state: RootState) =>
    state.qualityForm.data.isModified;

export const selectLoadError = (state: RootState) =>
    state.qualityForm.data.loadError;

export const getEvaluationSettings = (state: RootState) =>
    state.qualityForm.data.evaluationSettings;

export const selectIsFormWeighted = (state: RootState) => {
    const { weightMode } = selectForm(state) ?? {};
    const formType = selectFormType(state);
    return (
        weightMode === EvaluationFormWeightModeEnum.SCALED &&
        formType === FormType.EVALUATION
    );
};

export const selectQuestionTitle = (state: RootState, questionId: string) => {
    const { text } = selectQuestion(state, questionId) ?? { text: "" };
    return text;
};

export const selectQuestionHelpText = (
    state: RootState,
    questionId: string
) => {
    const { helpText } = selectQuestion(state, questionId) ?? {
        helpText: undefined,
    };
    return helpText;
};

export const selectIsAIScoringConfiguredForQuestion = (
    state: RootState,
    questionId: string
): boolean => {
    if (state.qualityForm.data.aiScoring) {
        const questionGroupSettings = Object.values(
            state.qualityForm.data.aiScoring?.questionGroupSettings
        );
        for (const questionGroupSetting of questionGroupSettings) {
            const questionSettings = questionGroupSetting.questionSettings;
            if (questionId in questionSettings) {
                return questionSettings[questionId].enabled;
            }
        }
    }

    return false;
};

export const selectQuestionSettingsIndicators = (
    state: RootState,
    questionId: string
) => {
    const {
        visibilityCondition,
        isCritical,
        isKill,
        naEnabled,
        commentsRequired,
        type,
    } = selectQuestion(state, questionId) ?? {
        isCritical: false,
        isKill: false,
        naEnabled: false,
        commentsRequired: false,
        type: undefined,
    };
    const visibilityConditionDependent = visibilityCondition !== undefined;
    const formData = state.qualityForm.data;
    const isMultipleSelect = type === BaseQuestionType.MultipleSelectQuestion;

    let isAssistanceConfigured;
    if (isMultipleSelect) {
        isAssistanceConfigured = formData?.questions?.[
            questionId
        ]?.multipleSelectOptionQuestions?.some((answerOptionId) => {
            const conditions = selectAnswerOption(
                state,
                answerOptionId,
                true
            )?.assistanceConditions;
            return conditions && conditions.length > 0;
        });
    } else {
        isAssistanceConfigured =
            formData?.questions?.[questionId]?.automatedScoringType ===
            AutomatedScoringType.EVALUATION_ASSISTANCE;
    }

    const isAiScoringConfigured = selectIsAIScoringConfiguredForQuestion(
        state,
        questionId
    );
    return {
        visibilityConditionDependent,
        isCritical,
        isKill,
        naEnabled,
        commentsRequired,
        isAssistanceConfigured,
        isAiScoringConfigured,
    };
};

export const selectQuestionGroup = (
    state: RootState,
    questionGroupId: string
) => {
    return state.qualityForm.data.questionGroups?.[questionGroupId];
};

export const selectQuestionGroupName = (
    state: RootState,
    questionGroupId: string
) => {
    return state.qualityForm.data.questionGroups?.[questionGroupId].name;
};

export const selectFormName = (state: RootState) =>
    state.qualityForm.data.form?.name;

export const selectLatestPublishedFormId = (state: RootState) =>
    state.qualityForm.data.form?.agreementRate?.latestPublishedFormId;

// TODO: MUST TEST WITH MULTISELECT FORM EDITOR PR
export const selectQuestionMaxScore = (
    state: RootState,
    questionId: string
) => {
    const isMultipleSelectFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.MULTIPLE_SELECT
    );
    const question = selectQuestion(state, questionId);
    if (!question) {
        return 0;
    }
    const type = question.type;
    if (
        isMultipleSelectFTEnabled &&
        type === BaseQuestionType.MultipleSelectQuestion
    ) {
        return question.multipleSelectOptionQuestions?.reduce(
            (sumScore: number, answerOptionId: string) => {
                const answerScore =
                    selectAnswerOption(state, answerOptionId, true)?.value ?? 0;
                return sumScore + answerScore;
            },
            0
        );
    } else {
        return question.answerOptions?.reduce(
            (maxScore: number, answerOptionId: string) => {
                const answerScore =
                    selectAnswerOption(state, answerOptionId)?.value ?? 0;
                return answerScore > maxScore ? answerScore : maxScore;
            },
            0
        );
    }
};

export const selectQuestionGroupMaxTotalScore = (
    state: RootState,
    questionGroupId: string
) => {
    return selectQuestionGroup(state, questionGroupId)?.questions?.reduce(
        (maxTotalScore, questionId) =>
            maxTotalScore + (selectQuestionMaxScore(state, questionId) ?? 0),
        0
    );
};

export const selectGroupWeight = (
    state: RootState,
    questionGroupId: string
) => {
    const questionGroup = selectQuestionGroup(state, questionGroupId);
    if (questionGroup !== undefined && questionGroup.weight !== undefined) {
        return calculateScalingFactorValue(questionGroup.weight);
    }
};

export const selectTotalGroupsPoints = (state: RootState) => {
    let totalQuestionGroupPoints = 0;
    const questionGroupIds = selectForm(state)?.questionGroups ?? [];
    questionGroupIds.map((questionGroupId) => {
        totalQuestionGroupPoints +=
            selectQuestionGroupMaxTotalScore(state, questionGroupId) ?? 0;
    });
    return totalQuestionGroupPoints;
};

export const selectQuestionGroupMaxTotalWeightedScore = (
    state: RootState,
    questionGroupId: string
) => {
    const groupWeight = selectGroupWeight(state, questionGroupId) ?? 1;
    const questionGroupTotalPoints =
        selectQuestionGroupMaxTotalScore(state, questionGroupId) ?? 0;
    return groupWeight * questionGroupTotalPoints;
};

export const selectTotalGroupsWeight = (state: RootState) => {
    let totalQuestionGroupWeight = 0;
    const questionGroupIds = selectForm(state)?.questionGroups ?? [];
    questionGroupIds.map((questionGroupId) => {
        totalQuestionGroupWeight +=
            selectGroupWeight(state, questionGroupId) ?? 0;
    });
    return totalQuestionGroupWeight;
};

export const selectTotalGroupsScaledPoints = (state: RootState) => {
    let totalQuestionGroupScaledPoints = 0;
    const questionGroupIds = selectForm(state)?.questionGroups ?? [];
    questionGroupIds.map((questionGroupId) => {
        totalQuestionGroupScaledPoints +=
            selectQuestionGroupMaxTotalWeightedScore(state, questionGroupId) ??
            0;
    });
    return totalQuestionGroupScaledPoints;
};
export const selectQuestionAnswer = (state: RootState, questionId: string) => {
    const question = state.qualityForm.data.questions?.[questionId];
    if (question?.type === BaseQuestionType.NpsQuestion) {
        const npsScore =
            selectNPSQuestionScoreAnswer(state, questionId) ?? undefined;
        if (npsScore === undefined) {
            return "";
        } else if (npsScore < 7) {
            return NPSScoreCategories.DETRACTOR;
        } else if (npsScore > 8) {
            return NPSScoreCategories.PROMOTER;
        } else {
            return NPSScoreCategories.PASSIVE;
        }
    } else if (question?.type === BaseQuestionType.MultipleSelectQuestion) {
        return selectCurrentAnswerIdsForMSQ(state, questionId);
    } else {
        return selectMCQQuestionScoreAnswer(state, questionId);
    }
};

export const selectIsVisible = createSelector(
    (
        state: RootState,
        combiningOperation?: VisibilityConditionCombiningOperationEnum,
        predicates?: Array<VisibilityConditionPredicate>,
        selfQuestionGroupId?: string
    ) => {
        return {
            state,
            combiningOperation,
            predicates,
            selfQuestionGroupId,
        };
    },
    ({
        state,
        combiningOperation,
        predicates,
        selfQuestionGroupId,
    }): boolean => {
        const hasConditions = !!(
            predicates &&
            predicates.length &&
            combiningOperation
        );

        if (!hasConditions) {
            return true;
        }
        // herein, we are skipping selectIsQuestionGroupVisible check on an entity's own question group.
        const isConditionSatisfied = ({
            questionId,
            answerId,
            questionGroupId,
        }: VisibilityConditionPredicate) => {
            const questionType = selectQuestion(state, questionId)?.type;
            const questionAnswer = selectQuestionAnswer(state, questionId);

            return (
                !selectQuestionGroupMarkedNa(state, questionGroupId) &&
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                (selfQuestionGroupId === questionGroupId ||
                    selectIsQuestionGroupVisible(
                        state,
                        questionGroupId,
                        false
                    )) &&
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                selectIsQuestionVisible(state, questionId) &&
                (questionType === BaseQuestionType.MultipleSelectQuestion
                    ? questionAnswer?.includes(answerId)
                    : answerId === questionAnswer)
            );
        };

        if (
            combiningOperation === VisibilityConditionCombiningOperationEnum.OR
        ) {
            return predicates.some(isConditionSatisfied);
        }
        if (
            combiningOperation === VisibilityConditionCombiningOperationEnum.AND
        ) {
            return predicates.every(isConditionSatisfied);
        }
        return false;
    }
);

export const selectIsQuestionVisible = (
    state: RootState,
    questionId: string
): boolean => {
    const { combiningOperation, predicates } =
        selectQuestion(state, questionId)?.visibilityCondition ?? {};
    const questionGroupId = Object.values(
        state.qualityForm.data.questionGroups ?? {}
    ).find((questionGroup) => questionGroup.questions.includes(questionId))?.id;
    return (
        selectIsVisible(
            state,
            combiningOperation,
            predicates,
            questionGroupId
        ) &&
        questionGroupId !== undefined &&
        selectIsQuestionGroupVisible(state, questionGroupId, true)
    );
};

export const selectQuestionIds = (
    state: RootState,
    questionGroupId: string
) => {
    return selectQuestionGroup(state, questionGroupId)?.questions ?? [];
};

export const selectVisibleQuestionIds = (
    state: RootState,
    questionGroupId: string
) => {
    if (selectQuestionGroupMarkedNa(state, questionGroupId)) {
        return [];
    }
    const questionList = selectQuestionIds(state, questionGroupId);
    return questionList.filter((questionId) =>
        selectIsQuestionVisible(state, questionId)
    );
};

export const selectDoesQuestionGroupHaveVisibleQuestions = (
    state: RootState,
    questionGroupId: string
) => {
    return (selectQuestionGroup(state, questionGroupId)?.questions ?? []).some(
        (questionId) => selectIsQuestionVisible(state, questionId)
    );
};

export const selectQuestionGroupIds = (state: RootState) => {
    return selectForm(state)?.questionGroups ?? [];
};

export const selectIsQuestionGroupVisible = createSelector(
    (state: RootState, questionGroupId: string, shallowCheck: boolean) => {
        return {
            state,
            questionGroupId,
            shallowCheck,
        };
    },
    ({ state, questionGroupId, shallowCheck }): boolean => {
        const { combiningOperation, predicates } =
            selectQuestionGroup(state, questionGroupId)?.visibilityCondition ??
            {};
        return (
            selectIsVisible(
                state,
                combiningOperation,
                predicates,
                questionGroupId
            ) &&
            (shallowCheck ||
                selectDoesQuestionGroupHaveVisibleQuestions(
                    state,
                    questionGroupId
                ))
        );
    }
);

export const selectVisibleQuestionGroupIds = createSelector(
    [selectQuestionGroupIds, (state: RootState) => state],
    (questionGroupList, state: RootState) => {
        if (!questionGroupList || !Array.isArray(questionGroupList)) {
            return [];
        }
        return questionGroupList.filter((questionGroupId) =>
            selectIsQuestionGroupVisible(state, questionGroupId, false)
        );
    }
);

/**
 * Checks if there are any visible critical questions in the form
 * @param state - The root state of the application
 * @returns Boolean indicating if there are any visible critical questions
 */
export const selectHasVisibleCriticalQuestions = (
    state: RootState
): boolean => {
    const visibleQuestionGroupIds = selectVisibleQuestionGroupIds(state);

    for (const groupId of visibleQuestionGroupIds) {
        const visibleQuestionIds = selectVisibleQuestionIds(state, groupId);

        if (hasAnyCriticalQuestions(state, visibleQuestionIds)) {
            return true;
        }
    }

    return false;
};

export const selectEligibleQuestionsForVisibilityConditionsForQuestion = (
    state: RootState,
    selectedQuestionId: string,
    filterOutConsumedQuestions: boolean
) => {
    const isMultipleSelectFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.MULTIPLE_SELECT
    );
    const eligibleQuestions: Array<string> = [];
    const qualityFormData = state.qualityForm.data;
    for (const questionGroupId of qualityFormData.form?.questionGroups ?? []) {
        for (const questionId of qualityFormData.questionGroups?.[
            questionGroupId
        ].questions ?? []) {
            if (questionId === selectedQuestionId) {
                return eligibleQuestions.length === 0
                    ? undefined
                    : eligibleQuestions;
            }
            const question = qualityFormData.questions?.[questionId];
            if (
                question !== undefined &&
                isVisibilityConditionEligibleQuestionType(
                    question.type,
                    isMultipleSelectFTEnabled
                ) &&
                (filterOutConsumedQuestions ||
                    (selectEligibleAnswersForVisibilityConditionInQuestion(
                        state.qualityForm,
                        questionId,
                        selectedQuestionId,
                        isMultipleSelectFTEnabled
                    )?.length ?? 0) > 0)
            ) {
                eligibleQuestions.push(questionId);
            }
        }
    }
};

const selectEligibleAnswersForVisibilityConditionInQuestion = (
    state: QualityFormState,
    questionIdToBeAdded: string,
    questionIdWithVisibilityConditions: string,
    isMultipleSelectFTEnabled: boolean
) => {
    const predicates = state.data.questions?.[
        questionIdWithVisibilityConditions
    ].visibilityCondition?.predicates.filter(
        (condition) => condition.questionId === questionIdToBeAdded
    );
    return selectEligibleAnswersForVisibilityCondition(
        state,
        questionIdToBeAdded,
        predicates,
        isMultipleSelectFTEnabled
    );
};
const selectEligibleAnswersForVisibilityConditionInQuestionGroup = (
    state: QualityFormState,
    questionIdToBeAdded: string,
    questionIdWithVisibilityConditions: string,
    isMultipleSelectFTEnabled: boolean
) => {
    const predicates = state.data.questionGroups?.[
        questionIdWithVisibilityConditions
    ].visibilityCondition?.predicates.filter(
        (condition) => condition.questionId === questionIdToBeAdded
    );
    return selectEligibleAnswersForVisibilityCondition(
        state,
        questionIdToBeAdded,
        predicates,
        isMultipleSelectFTEnabled
    );
};
const selectEligibleAnswersForVisibilityCondition = (
    state: QualityFormState,
    questionIdToBeAdded: string,
    predicates?: VisibilityConditionPredicate[],
    isMultipleSelectFTEnabled?: boolean
) => {
    const questionToBeAdded = state.data.questions?.[questionIdToBeAdded];
    const questionType = questionToBeAdded?.type ?? undefined;
    if (
        isMultipleSelectFTEnabled &&
        questionType === EvaluationQuestionTypeEnum.MultipleSelectQuestion
    ) {
        const mSelectOptionsQs =
            questionToBeAdded?.multipleSelectOptionQuestions ?? [];
        if (predicates && predicates.length > 0) {
            const answersInPredicates = new Set(
                predicates.map((condition) => condition.answerId)
            );
            return mSelectOptionsQs.filter(
                (answerId) => !answersInPredicates.has(answerId)
            );
        }
        return mSelectOptionsQs;
    }

    // we filter out all the answers from "questionToBeAdded" which are already present in the predicates
    // (within the question being editted).
    if (questionType === SurveyQuestionTypeEnum.NpsQuestion) {
        const npsValues = Object.values(NPSScoreCategories);
        if (predicates && predicates.length > 0) {
            const answersInPredicates = new Set(
                predicates.map((condition) => condition.answerId)
            );
            return (
                npsValues.filter(
                    (npsValue) => !answersInPredicates.has(npsValue)
                ) ?? []
            );
        }
        return npsValues;
    } else {
        const answerOptions = questionToBeAdded?.answerOptions ?? [];
        if (predicates && predicates.length > 0) {
            const answersInPredicates = new Set(
                predicates.map((condition) => condition.answerId)
            );
            return answerOptions.filter(
                (answerId) => !answersInPredicates.has(answerId)
            );
        }
        return answerOptions;
    }
};

export const selectEligibleQuestionsForVisibilityConditionsForQuestionGroup = (
    state: RootState,
    selectedQuestionGroupId: string,
    includeConsumedQuestions: boolean
) => {
    const isMultipleSelectFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.MULTIPLE_SELECT
    );
    const eligibleQuestions: Array<string> = [];
    const qualityFormData = state.qualityForm.data;
    for (const questionGroupId of qualityFormData.form?.questionGroups ?? []) {
        if (questionGroupId === selectedQuestionGroupId) {
            return eligibleQuestions.length === 0
                ? undefined
                : eligibleQuestions;
        }
        for (const questionId of qualityFormData.questionGroups?.[
            questionGroupId
        ].questions ?? []) {
            const question = qualityFormData.questions?.[questionId];
            if (
                question !== undefined &&
                isVisibilityConditionEligibleQuestionType(
                    question.type,
                    isMultipleSelectFTEnabled
                ) &&
                (includeConsumedQuestions ||
                    selectEligibleAnswersForVisibilityConditionInQuestionGroup(
                        state.qualityForm,
                        questionId,
                        selectedQuestionGroupId,
                        isMultipleSelectFTEnabled
                    ).length > 0)
            ) {
                eligibleQuestions.push(questionId);
            }
        }
    }
};
export const selectMaxResponseCharacters = (
    state: RootState,
    questionId: string
) => {
    return selectQuestion(state, questionId)?.maxResponseCharacters ?? 0;
};

export const getDenormalizedQualityForm = (state: RootState) => {
    const formId = state.qualityForm.data.form?.id;
    if (formId) {
        // denormalize
        const { form, ...rest } = state.qualityForm.data;
        // denormalized returns something that is like BaseForm, except the visibility conditions are really VisibilityConditionPredicate,
        // but we will be converting them
        // to be string[] since that is what BaseForm says they are, we are okay with this lie
        let denormalizedForm:
            | EvaluationFormResponse
            | AssessmentForm
            | SurveyForm;

        if (rest.formType === FormType.ASSESSMENT) {
            denormalizedForm = AssessmentFormFromJSON(
                denormalize(formId, normalizedQualityForm, {
                    form: { [formId]: form },
                    ...rest,
                })
            );

            // Add division object if present
            const formDivision = (form as any)?.division;
            if (formDivision) {
                (denormalizedForm as any).division = formDivision;
            }
        } else if (rest.formType === FormType.SURVEY) {
            denormalizedForm = SurveyFormFromJSON(
                denormalize(formId, normalizedQualityForm, {
                    form: { [formId]: form },
                    ...rest,
                })
            );

            // Add division object if present
            const formDivision = (form as any)?.division;
            if (formDivision) {
                (denormalizedForm as any).division = formDivision;
            }
        } else {
            denormalizedForm = EvaluationFormResponseFromJSON(
                denormalize(formId, normalizedQualityForm, {
                    form: { [formId]: form },
                    ...rest,
                })
            );

            // Add division object if present
            const formDivision = (form as any)?.division;
            if (formDivision) {
                (denormalizedForm as any).division = formDivision;
            }

            const isAutomatedEvaluationForm =
                selectIsAutomatedEvaluationForm(state);

            denormalizedForm.evaluationSettings = toFormEvaluationSettings(
                state.qualityForm.data.evaluationSettings
            );
            if (isAutomatedEvaluationForm) {
                delete denormalizedForm.evaluationSettings.revisionsEnabled;
                delete denormalizedForm.evaluationSettings.disputesEnabled;
                delete denormalizedForm.evaluationSettings.disputesAssignees;
                delete denormalizedForm.evaluationSettings
                    .disputesAllowedPerEvaluation;
            }
        }

        // Check feature toggle for default answering
        const isDefaultAnsweringEnabled = selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
        );

        const shouldClearDefaultAnswers =
            (rest.formType === FormType.EVALUATION &&
                !isDefaultAnsweringEnabled) ||
            rest.formType !== FormType.EVALUATION;

        // Hint for how to convert to group index, question index
        denormalizedForm.questionGroups?.forEach((questionGroup) => {
            if (questionGroup.visibilityCondition) {
                questionGroup.visibilityCondition.predicates =
                    (selectQuestionGroupPredicatePaths(
                        state,
                        questionGroup.id!
                    ) ?? []) as unknown as object[];
            }

            if (shouldClearDefaultAnswers) {
                delete questionGroup.defaultAnswersTo;
            }

            if (questionGroup.questions) {
                questionGroup.questions.forEach((question) => {
                    if (question.visibilityCondition?.predicates) {
                        question.visibilityCondition.predicates =
                            (selectQuestionPredicatePaths(
                                state,
                                question.id!
                            ) ?? []) as unknown as object[];
                    }
                    if (shouldClearDefaultAnswers) {
                        if (question) {
                            question.defaultAnswer = undefined;
                        }
                        // Also clear default answers for multiple select questions
                        question?.multipleSelectOptionQuestions?.forEach(
                            (msQuestion) => {
                                if (msQuestion) {
                                    msQuestion.defaultAnswer = undefined;
                                }
                            }
                        );
                    }
                });
            }
        });

        return denormalizedForm;
    }
};

export const selectPredicatePath = (
    state: RootState,
    predicate: VisibilityConditionPredicate
) => {
    const answerOptions =
        selectAnswerOptionsForQuestion(state, predicate.questionId) ?? [];
    const questions =
        selectQuestionGroup(state, predicate.questionGroupId)?.questions ?? [];
    const questionGroups = selectForm(state)?.questionGroups ?? [];
    const answerIndex =
        predicate.answerId !== "PROMOTER" &&
        predicate.answerId !== "PASSIVE" &&
        predicate.answerId !== "DETRACTOR"
            ? answerOptions.indexOf(predicate.answerId)
            : predicate.answerId;
    const questionIndex = questions.indexOf(predicate.questionId);
    const questionGroupIndex = questionGroups.indexOf(
        predicate.questionGroupId
    );

    if (
        answerIndex === -1 ||
        questionIndex === -1 ||
        questionGroupIndex === -1
    ) {
        throw new Error(
            " visibility condition predicates couldn't be denormalized"
        );
    }
    // This call is not that expensive but put it after denormalizing check anyways.
    // Also not checking for multiple select FT here because having multiple select question
    // here must already pass FT.
    let multipleSelectPath = "";
    const questionType = selectQuestion(state, predicate.questionId)?.type;
    if (
        questionType &&
        questionType === BaseQuestionType.MultipleSelectQuestion
    ) {
        multipleSelectPath = "/Selected";
    }
    return `/form/questionGroup/${questionGroupIndex}/question/${questionIndex}/answer/${answerIndex}${multipleSelectPath}`;
};

function selectPredicatePaths({
    state,
    visibilityConditions,
}: {
    state: RootState;
    visibilityConditions: ExpandedVisibilityCondition | undefined;
}) {
    if (!visibilityConditions || visibilityConditions.predicates.length === 0) {
        return;
    }
    return visibilityConditions.predicates
        .filter(
            (predicate) =>
                predicate.answerId &&
                predicate.questionId &&
                predicate.questionGroupId
        )
        .map((predicate) => selectPredicatePath(state, predicate));
}

export const selectQuestionPredicatePaths = (state: RootState, id: string) => {
    return selectPredicatePaths({
        state,
        visibilityConditions: selectQuestion(state, id)?.visibilityCondition,
    });
};

export const selectQuestionGroupPredicatePaths = (
    state: RootState,
    id: string
) => {
    return selectPredicatePaths({
        state,
        visibilityConditions: selectQuestionGroup(state, id)
            ?.visibilityCondition,
    });
};

export const selectDependentVisibilityConditionForEntity = createSelector(
    (
        state: QualityFormState,
        entityId: string,
        entityType: EntityType,
        isMultipleSelect: boolean
    ) => {
        return {
            qualityForm: state.data,
            entityId: entityId,
            entityType: entityType,
            isMultipleSelect,
        };
    },
    ({ qualityForm, entityId, entityType }) => {
        const dependentEntities: DependentIds = {
            questionGroupIds: [],
            questionIds: [],
        };
        Object.values(qualityForm.questionGroups ?? {}).forEach(
            (questionGroup) => {
                questionGroup.visibilityCondition?.predicates.forEach(
                    (predicate) => {
                        if (
                            selectIsFormIdUsedInPredicate(
                                predicate,
                                entityType,
                                entityId
                            ) &&
                            questionGroup.id
                        ) {
                            dependentEntities.questionGroupIds.push(
                                questionGroup.id
                            );
                        }
                    }
                );

                // loop all questions
                questionGroup.questions.forEach((questionId) => {
                    const question = qualityForm.questions?.[questionId];

                    question &&
                        question.visibilityCondition?.predicates.forEach(
                            (predicate) => {
                                if (
                                    selectIsFormIdUsedInPredicate(
                                        predicate,
                                        entityType,
                                        entityId
                                    ) &&
                                    question.id
                                ) {
                                    dependentEntities.questionIds.push(
                                        question.id
                                    );
                                }
                            }
                        );
                });
            }
        );
        return dependentEntities;
    }
);

const selectIsFormIdUsedInPredicate = (
    predicate: VisibilityConditionPredicate,
    entityType: EntityType,
    entityId: string
) => {
    return (
        (entityType === EntityType.Answer && predicate.answerId === entityId) ||
        (entityType === EntityType.Question &&
            predicate.questionId === entityId) ||
        (entityType === EntityType.QuestionGroup &&
            predicate.questionGroupId === entityId)
    );
};

export const selectQuestionGroupSequenceNumber =
    (state: QualityFormState) => (questionGroupId: string) => {
        const questionGroupIndex =
            state.data.form?.questionGroups?.indexOf(questionGroupId);
        if (questionGroupIndex !== undefined) {
            return questionGroupIndex + 1;
        }
    };

export const selectQuestionSequenceNumber =
    (state: QualityFormState) => (questionId: string) => {
        const questionGroup = Object.values(
            state.data.questionGroups ?? {}
        ).find((questionGroup) => questionGroup.questions.includes(questionId));
        if (questionGroup && questionGroup.id) {
            return {
                questionSequenceNumber:
                    questionGroup.questions.indexOf(questionId) + 1,
                questionGroupNumber: selectQuestionGroupSequenceNumber(state)(
                    questionGroup.id
                ),
            };
        }
    };

const selectVisibilityDependentsForQuestionGroupToMoveUp =
    (upperQuestionGroupId: string, lowerQuestionGroupId: string) =>
    (state: QualityFormState) => {
        const visibilityConflictsOnMove: VisibilityConflictsOnMove = {
            questionIds: [],
            conflictingQuestionsWithinQuestionGroup: {},
        };
        const questionGroup = state.data.questionGroups?.[lowerQuestionGroupId];

        questionGroup?.visibilityCondition?.predicates.forEach((pred) => {
            if (pred.questionGroupId === upperQuestionGroupId) {
                visibilityConflictsOnMove.questionIds.push(pred.questionId);
            }
        });

        questionGroup?.questions.forEach((questionId) => {
            state.data.questions?.[
                questionId
            ].visibilityCondition?.predicates.forEach((pred) => {
                if (pred.questionGroupId === upperQuestionGroupId) {
                    if (
                        visibilityConflictsOnMove
                            .conflictingQuestionsWithinQuestionGroup?.[
                            questionId
                        ]
                    ) {
                        visibilityConflictsOnMove.conflictingQuestionsWithinQuestionGroup?.[
                            questionId
                        ].push(pred.questionId);
                    } else if (
                        visibilityConflictsOnMove.conflictingQuestionsWithinQuestionGroup
                    ) {
                        visibilityConflictsOnMove.conflictingQuestionsWithinQuestionGroup[
                            questionId
                        ] = [pred.questionId];
                    }
                }
            });
        });

        return visibilityConflictsOnMove;
    };

const selectIsQuestionValidToMoveUpWithVisibilityCondition =
    (upperQuestionId: string, lowerQuestionId: string) =>
    (state: QualityFormState) => {
        const question = state.data.questions?.[lowerQuestionId];
        return !question?.visibilityCondition?.predicates.some(
            (pred) => pred.questionId === upperQuestionId
        );
    };

export const selectQuestionGroupIdContainingQuestion =
    (questionId: string) => (state: QualityFormState) => {
        const questionGroups = state.data.questionGroups;
        const questionGroupId = questionGroups
            ? Object.values(questionGroups).find((qg) =>
                  qg.questions.includes(questionId)
              )?.id
            : undefined;
        return questionGroupId;
    };

export const selectAssistedConditionForAnswer = (
    answerOptionId: string,
    state: RootState,
    isMultipleSelect?: boolean
) => {
    const answerOption = selectAnswerOption(
        state,
        answerOptionId,
        !!isMultipleSelect
    );

    return answerOption?.assistanceConditions;
};

const selectHasEditPermissions = (state: RootState) => {
    const formType = selectFormType(state);
    return (
        (formType === FormType.EVALUATION &&
            selectPermission(state, "quality:evaluationForm:edit")) ||
        (formType === FormType.SURVEY &&
            selectPermission(state, "quality:surveyForm:edit")) ||
        (formType === FormType.ASSESSMENT &&
            selectPermission(state, "learning:module:edit"))
    );
};

export const selectIsEvaluationSettingError = (state: RootState) => {
    return (
        state.qualityForm.data.evaluationSettings?.disputeAssignments.some(
            (assignment) =>
                assignment.assigneeUserError &&
                assignment.type ===
                    EvaluationSettingsAssigneeTypeEnum.Individual
        ) ?? false
    );
};

export const selectHasEditPermissionsForAutomatedEvaluationForms = (
    state: RootState
) => {
    const formType = selectFormType(state);
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);

    return (
        formType === FormType.EVALUATION &&
        isAutomatedEvaluationForm &&
        (selectHasAIScoringEditPermission(state) ||
            selectCanUserViewSTATopics(state))
    );
};

export const selectCanEditForm = (state: RootState) => {
    const formId = selectForm(state)?.id;
    const formType = selectFormType(state);
    const isExistingForm = formId !== "new" && formId !== undefined;
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);

    let isAdditionalConditionToShowEditableViewMet = true;
    if (isExistingForm) {
        isAdditionalConditionToShowEditableViewMet =
            formType === FormType.ASSESSMENT
                ? false
                : selectIsInCopyingViewMode(state);
    }
    return (
        (!selectForm(state)?.published || false) &&
        ((selectHasEditPermissions(state) &&
            (!isAutomatedEvaluationForm ||
                selectHasEditPermissionsForAutomatedEvaluationForms(state))) ||
            (selectHasAddPermissions(state) &&
                isAdditionalConditionToShowEditableViewMet))
    );
};

export const selectCanDisableForm = (state: RootState) => {
    return (
        selectHasEditPermissions(state) &&
        (selectForm(state)?.published ?? false) &&
        selectFormType(state) === FormType.SURVEY &&
        selectPermission(state, "quality:surveyForm:disable")
    );
};

export const selectCanDeleteEvaluation = (state: RootState) => {
    const formType = selectFormType(state);
    return (
        (formType === FormType.EVALUATION &&
            selectScorable(state)?.calibration &&
            selectPermission(state, "quality:calibration:delete")) ||
        (formType === FormType.EVALUATION &&
            !selectScorable(state)?.calibration &&
            selectPermission(state, "quality:evaluation:delete"))
    );
};

export const selectTotalVisibleQuestionsWithAssistance = (state: RootState) => {
    const questionIds =
        selectTotalVisibleQuestionsWithAssistanceConditions(state);
    return questionIds.filter((questionId) => {
        const question = selectQuestion(state, questionId);
        if (question?.type === BaseQuestionType.MultipleSelectQuestion) {
            // iterate state.scorable.data.questionScores[questionId]?.multipleSelectQuestionOptionScores
            // and check whether the length of the items with defined assistedAnswerId is greater than 0 or not
            const multipleSelectOptionScores =
                state.scorable.data.questionScores[questionId]
                    ?.multipleSelectQuestionOptionScores;
            return (
                multipleSelectOptionScores &&
                multipleSelectOptionScores.some(
                    (score) =>
                        !isUndefinedOrNull(score.assistedAnswerId) &&
                        score.assistedAnswerId !== ""
                )
            );
        } else {
            const assistedAnswerId =
                state.scorable.data.questionScores[questionId]
                    ?.assistedAnswerId;
            return (
                !isUndefinedOrNull(assistedAnswerId) && assistedAnswerId !== ""
            );
        }
    }).length;
};

export const selectTotalAiScoringConfiguredQuestions = (
    state: RootState
): number => {
    const isAiScoringFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.AI_SCORING
    );

    if (isAiScoringFTEnabled && state.qualityForm.data.aiScoring) {
        const questionGroupSettings = Object.values(
            state.qualityForm.data.aiScoring.questionGroupSettings
        );

        return questionGroupSettings
            .flatMap((group) => Object.values(group.questionSettings))
            .filter((question) => question.enabled).length;
    } else {
        return 0;
    }
};

export const selectAnswerOptionsWithAssistance = (state: RootState) => {
    const answers = state.qualityForm.data.answerOptions ?? {};
    return Object.keys(answers).filter(
        (answerId) => (answers[answerId].assistanceConditions ?? []).length > 0
    );
};

export const selectQuestionsWithAssistance = (state: RootState) => {
    const questions = state.qualityForm.data.questions ?? {};
    // iterate every question
    // if it is MCQ type, then check its answerOptions item
    // if it is MSQ type, then check its multipleSelectQuestionOptions item
    return Object.keys(questions).filter((questionId) => {
        const question = questions[questionId];
        if (question.type === BaseQuestionType.MultipleChoiceQuestion) {
            return (question.answerOptions ?? []).some(
                (answerOptionId) =>
                    (
                        selectAssistedConditionForAnswer(
                            answerOptionId,
                            state,
                            false
                        ) ?? []
                    ).length > 0
            );
        } else if (question.type === BaseQuestionType.MultipleSelectQuestion) {
            return (question.multipleSelectOptionQuestions ?? []).some(
                (multipleSelectOptionQuestionId) =>
                    (
                        selectAssistedConditionForAnswer(
                            multipleSelectOptionQuestionId,
                            state,
                            true
                        ) ?? []
                    ).length > 0
            );
        } else {
            return false;
        }
    });
};

export const selectAnswerOptionsWithAssistanceForQuestion = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    let answerOptions: string[] = [];
    const isMultipleSelect =
        question?.type === BaseQuestionType.MultipleSelectQuestion;
    if (isMultipleSelect) {
        answerOptions = question?.multipleSelectOptionQuestions ?? [];
    } else {
        answerOptions = question?.answerOptions ?? [];
    }

    return answerOptions?.filter((answerOption) => {
        return (
            selectAssistedConditionForAnswer(
                answerOption,
                state,
                isMultipleSelect
            ) !== undefined
        );
    });
};

export const selectTotalVisibleQuestionsWithAssistanceConditions = (
    state: RootState
) => {
    const questionIds = selectTotalVisibleQuestions(state);
    return questionIds.filter((questionId) => {
        return (
            selectAnswerOptionsWithAssistanceForQuestion(state, questionId)
                ?.length !== 0
        );
    });
};

export const selectTotalVisibleQuestions = (state: RootState) => {
    return selectVisibleQuestionGroupIds(state)
        .map((questionGroupId) =>
            selectVisibleQuestionIds(state, questionGroupId)
        )
        .flat();
};

export const selectTotalVisibleQuestionsWithAiScoring = (state: RootState) => {
    const questionIds = selectTotalVisibleQuestions(state);
    return questionIds.filter((questionId) => {
        return selectIsAIScoringConfiguredForQuestion(state, questionId);
    });
};

export const selectTopicFilteringFactorFromAssistanceConditions = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    const assistanceConditions = selectAssistanceConditions(
        state,
        answerId,
        isMultipleSelect
    );
    const resultingMap: { [key: string]: number } = {};
    const filteringFactor: TopicFilteringFactor = {
        topicIdIdxMap: resultingMap,
        hasSameTopicInDifferentConditions: false,
    };
    assistanceConditions?.forEach((condition, conditionIdx) => {
        condition.topicIds?.some((topicId: string) => {
            if (resultingMap[topicId] !== undefined) {
                return (filteringFactor.hasSameTopicInDifferentConditions =
                    true);
            } else {
                resultingMap[topicId] = conditionIdx;
                return false;
            }
        });
    });
    return filteringFactor;
};

export const selectNpsPrompt = (state: RootState, questionId: string) => {
    return selectQuestion(state, questionId)?.explanationPrompt;
};

export const selectDoesNpsQuestionAlreadyExist = (state: RootState) => {
    const isMultipleSelectFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.MULTIPLE_SELECT
    );

    const formType = selectFormType(state);
    if (formType) {
        const questionGroupIds =
            state.qualityForm.data.form?.questionGroups ?? [];
        return questionGroupIds.some((questionGroupId) => {
            const questionIds =
                selectQuestionGroup(state, questionGroupId)?.questions ?? [];
            const questionTypesMap = getQuestionTypesMap(
                isMultipleSelectFTEnabled
            );
            return questionIds?.some((questionId) => {
                return (
                    selectQuestion(state, questionId)?.type ===
                    questionTypesMap[formType][QuestionTypesMapSurveyIndex.NPS]
                        .questionType
                );
            });
        });
    }
};

export const selectIsEvaluationAssistanceConfiguredForQuestion = (
    state: RootState,
    questionId: string
) => {
    const answerOptionsWithAssistanceConfigured =
        selectAnswerOptionsWithAssistanceForQuestion(state, questionId);
    return answerOptionsWithAssistanceConfigured?.length !== 0;
};

export const selectIsEvaluationAssistanceConfigured = (state: RootState) => {
    return Object.values(state.qualityForm.data?.questions ?? {}).some(
        (question) => {
            return selectIsEvaluationAssistanceConfiguredForQuestion(
                state,
                question.id ?? ""
            );
        }
    );
};

export const selectAutomatedScoringOption = (
    state: RootState,
    questionId: string
) => {
    const isEvaluationAssistanceConfigured =
        selectIsEvaluationAssistanceConfiguredForQuestion(state, questionId);

    const isAIScoringConfigured = selectIsAIScoringConfiguredForQuestion(
        state,
        questionId
    );
    if (isEvaluationAssistanceConfigured) {
        return AutomatedScoringType.EVALUATION_ASSISTANCE;
    } else if (isAIScoringConfigured) {
        return AutomatedScoringType.AI_SCORING;
    } else {
        return AutomatedScoringType.NO_AUTOMATION;
    }
};

export const selectIsAiScoringConfiguredForEvaluation = (state: RootState) => {
    // TODO: might convert to state.qualityForm.data.aiScoring or update once API evaluation response returns aiScoring field in the evaluation form.
    const aiScoring = state.qualityForm.data.form?.aiScoring;
    const questionGroupSettings = aiScoring?.questionGroupSettings ?? [];
    const enabledQuestions = questionGroupSettings
        .flatMap((questionGroup) => questionGroup.questionSettings ?? [])
        .some((question) => question.settings?.enabled === true);
    return enabledQuestions;
};

export const selectFormLanguage = (state: RootState) => {
    const language = state.qualityForm.data.form?.language;
    return language as Locale | undefined;
};

export const selectIsFormPublished = (state: RootState) => {
    return state.qualityForm.data.form?.published;
};

export const selectIsFormDisabled = (state: RootState) => {
    return state.qualityForm.data.form?.disabled;
};

export const selectResourceCenterUri = (state: RootState) => {
    const isResourceCenterFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.NEW_RESOURCE_CENTER_URI
    );
    if (isResourceCenterFTEnabled) {
        return "https://help.genesys.cloud/articles/format-the-header-and-footer-in-a-survey-form/";
    } else {
        return "https://help.mypurecloud.com/articles/format-the-header-and-footer-in-a-survey-form/";
    }
};

export const selectEnableReportingViewRedirects = (state: RootState) => {
    const isReportingFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.REPORTING_TOGGLE
    );
    const isPublishedForm = selectForm(state)?.published ?? false;
    const formType = selectFormType(state);
    const isPublishedEvaluationForm =
        isPublishedForm && formType === FormType.EVALUATION;
    return (
        isReportingFeatureEnabled &&
        isPublishedEvaluationForm &&
        selectHasQualityEvaluationSearchAny(state) &&
        !selectIsInCopyingViewMode(state)
    );
};

export const selectIsUserDefinedSelectedForQuestionGroup = (
    state: RootState,
    questionGroupId: string
) => {
    const questionGroup = selectQuestionGroup(state, questionGroupId);

    return !!questionGroup?.defaultAnswersTo?.userDefined;
};

// For evaluation forms with WEM-1359 feature toggle enabled
export const selectEvaluationFormDefaultAnswers = (
    state: RootState,
    questionGroupId: string
) => {
    const questionGroup = selectQuestionGroup(state, questionGroupId);
    const isDefaultAnsweringEnabled = selectFeatureToggle(
        state,
        FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
    );

    if (
        isDefaultAnsweringEnabled &&
        state.qualityForm.data.formType === FormType.EVALUATION &&
        questionGroup
    ) {
        return {
            highestScore:
                questionGroup?.defaultAnswersTo?.highestScore ?? false,
            lowestScore: questionGroup?.defaultAnswersTo?.lowestScore ?? false,
            userDefined: questionGroup?.defaultAnswersTo?.userDefined ?? false,
            notApplicable:
                questionGroup?.defaultAnswersTo?.notApplicable ?? false,
        };
    }
};

// For legacy behavior
export const selectLegacyDefaultAnswers = (
    state: RootState,
    questionGroupId: string
) => {
    const questionGroup = selectQuestionGroup(state, questionGroupId);
    const isDefaultAnsweringEnabled = selectFeatureToggle(
        state,
        FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
    );

    if (
        questionGroup &&
        (!isDefaultAnsweringEnabled ||
            state.qualityForm.data.formType !== FormType.EVALUATION)
    ) {
        return {
            defaultAnswersToHighest:
                questionGroup.defaultAnswersToHighest ?? false,
            defaultAnswersToNA: questionGroup.defaultAnswersToNA ?? false,
        };
    }
};

export default slice.reducer;
